<template>
<div class="chapter">
<div class="inner_page">
<p class="image"><img alt="image" class="full" src="images/0011.jpg"/></p>
<p class="image"><img alt="image" class="full" src="images/0012.jpg"/></p>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="intro0" id="i2203a">The tradition of decorating a tree for Christmas is universal, though every culture has its own twist. In Scandinavia,
ornaments may be woven from straw; Eastern Europe is
famous for handpainted eggs; and those in Japan who decorate Christmas trees do so with tiny fans, paper lanterns, and
origami swans. Here, Leigh Radford uses wool. In one
version, her ornaments are simple balls knit in solid colors
or stripes (see left). In the other, colored fleece is felted into
small balls that are joined and embellished in myriad ways—
even strung together into a garland <a href="#/Chapter1#feltedOrn">(see pages 14 and  15)</a>.</p>
<p class="title" id="i1540a">Knitted Ornaments</p>
<p class="title2" id="i1540b">Finished Measurements</p>
<p class="pgph1" id="i1540c">Small (Large): Approximately 3 ( 4 <span class="ch_6"> ¼</span> )" diameter</p>
<p class="title2" id="i1540d">

Yarn</p>
<p class="pgph1 wsp" id="i1540e">Harrisville Highland Style (100% wool;
200 yards / 100 grams): 1 skein each #4 gold, 
#17 Bermuda blue, #16 teal blue, #22 plum, 
#7 tundra (gold/green), #48 dove gray, 
#38 teak (brown)</p>
<p class="title2" id="i1540f">

Needles</p>
<p class="pgph1" id="i1540g">One set of five double-pointed needles (dpn) 
size US 7 (4.5 mm)</p>
<p class="pgph1" id="i1540h">Change needle size if necessary to obtain 
correct gauge.</p>
<p class="title2" id="i1540i">

Notions</p>
<p class="pgph1" id="i1540j">Crochet hook size F/5 (3.75 mm)
Polyester batting, yarn needle</p>
<p class="title2" id="i1540k">

Gauge</p>
<p class="pgph1" id="i1540l">24 sts and 28 rows = 4" (10 cm) in Stockinette stitch (St st)</p>
<p class="pgph2" id="i1499b">
CO 12 (20) sts; distribute sts evenly over 4 needles—3 (5) sts each needle.</p>
<p class="pgph2" id="i1499c">Join for working in the rnd, being careful not to twist sts; place marker (pm) for beginning of rnd.</p>
<p class="title3">Bottom</p>
<p class="pgph2" id="i1499d">Rnd 1: Knit.</p>
<p class="pgph2" id="i1499e">Rnd 2: [K1-f/b, knit across to last st on needle, k1-f/b] 4 times—20 (28) sts.</p>
<p class="pgph2" id="i1499f">Repeat Rnds 1 and 2 until there are 11 (15) sts on each needle—44 (60) sts.</p>
<p class="pgph2" id="i1499g">Work even for 5 (9) rnds.</p>
<p class="title3">
Top</p>
<p class="pgph2" id="i1499h">Rnd 1: [Skp, knit across to last 2 sts on needle, k2tog] 4 times—36 (52) sts remain.</p>
<p class="pgph2" id="i1499i">Rnd 2: Knit.</p>
<p class="pgph2" id="i1499j">Repeat Rnds 1 and 2 until 3 (5) sts remain on each needle, end after completing Rnd 2—12 (20) sts remain.</p>
<p class="pgph2" id="i1499k">Break yarn, leaving an 18" tail. With tail threaded onto yarn needle, slip sts onto tail—DO NOT gather at this point.</p>
<p class="title3">
Finishing </p>
<p class="pgph2" id="i1499l">Using CO tail threaded on yarn needle, weave yarn through CO edge; gently pull to gather closed. Weave in end securely on WS. Stuff ornament with batting. Gather live sts at BO edge and gently pull to close opening. Using BO tail and crochet hook, work a 4  <span class="ch_7">½</span>" chain; fasten off, leaving a tail for attaching chain to ball. Secure end of chain to top of ornament to form a loop. Embellish ornaments with embroidery if desired.</p>
<p class="pgph2" id="i1499a"><span class="ch_5">Note: Work ornaments in solid colors or in stripes, as desired.</span></p>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="image"><img alt="image" class="full" src="images/0014.jpg"/></p>
<p class="noindent"> </p>
<div id="u8c23">
<a id="feltedOrn"></a><p class="title" id="i1759y">Felted Ornaments and Garland</p>
<p class="title2" id="i1760a">Finished measurements</p>
<p class="pgph1" id="i1760b">Small (Large): approximately   <span class="ch_6">½</span>(1)" diameter</p>
<p class="title2" id="i1760c">Fleece</p>
<p class="pgph1 wsp" id="i1760d">Harrisville Fleece (100% wool): 1 ounce each
#4 gold, #17 Bermuda blue, #16 teal blue,
#22 plum, #7 tundra, #45 dove gray, #38 teak,
#8 hemlock</p>
<p class="title2" id="i1760e">Materials</p>
<p class="pgph1" id="i1760f">Felting needle (available at yarn stores and from Harrisville Design; see Sources for Supplies, page <span href="#/Supplies" title="Link target outside of Sample">184</span>)</p>
<p class="pgph1" id="i1760g">Pan or tub for water, dishwashing soap, rubber gloves with textured palms (for friction and faster felting)</p>
<p class="pgph1" id="i1760h">Leather lacing—18 feet or desired length for garland; 8 –10" for each ornament Yarn needle, sequin pins, seed beads—size 11 or smaller works best (bead must be small enough not to slip off the head of the pin), craft glue</p>
<p class="title3" id="i2473a">Solid-Color Felt Balls</p>
<p class="pgph2" id="i2473b">Working close to a sink, fill pan or tub with a couple inches of very hot, soapy water. 
Select a small section of fleece in desired color—filling the palm of your hand. Put on rubber gloves, tear fleece into small sections, 
and begin to roll the pieces together in a circular fashion between your palms. Dip the fleece into the hot, soapy water and continue to roll the now wet fleece in your hands, adding additional pieces of torn fleece if you would like to increase the size of the ball. Occasionally run your ball under very cold water—the “shock” of switching the fleece from hot to cold increases the speed with which it will felt. Continue to roll the ball 
between your palms until it reaches its desired smoothness and size. If felting time is slowing down, use hotter water and/or more soap.</p>
<p class="title3" id="i2473c">Multicolored Felt Balls</p>
<p class="pgph2" id="i2473d">Follow felting instructions above until ball is 
partially felted. Once the shape of the ball 
is formed, choose contrasting color(s) (CC), dampen in the soap and water, and place in the desired position on the partially felted ball. 
Using the felting needle, with a gentle stabbing motion, secure the fleece into place.  <span class="ch_5">(Note: Felting needles are very sharp and have a barbed point; the barbed point helps to stick 
the fleece together.)</span> Continue stabbing until the 
CC is tacked into place in desired shape, 
then continue felting as above until the ball has reached desired smoothness and size.</p>
<p class="title3" id="i2473e">Finishing</p>
<p class="pgph2" id="i2473f"><span class="ch_8">Beaded Embellishment:</span> Coat a short pin 
with a bit of glue, then slip seed bead onto 
pin and insert into felt ball in desired location.</p>
<p class="pgph2" id="i2473g"><span class="ch_8">Ornaments:</span> Using  8 – 10" of leather threaded onto yarn needle, insert needle up through one or more felted balls, then back down through in the opposite direction, leaving a 2 <span class="ch_7">½</span>" loop at the top. Secure leather by knotting both ends at 
the base of the ornament. Embellish as desired.</p>
<p class="pgph2" id="i2473h"><span class="ch_8">Garland:</span> Thread leather onto yarn needle; 
string felted balls onto leather, spacing them 
approximately 2 <span class="ch_7">½</span>" apart; tie a knot on either side of each ball to secure in place.</p>
</div>
<div id="ua19">
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="image"><img alt="image" class="full" src="images/0015.jpg"/></p>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div id="ub48">
<a id="garlands"></a>
<a id="pompomGarland"></a><p class="name pink" id="i738a">Amanda Blair Brown</p>
<p class="h1 red" id="i738a2">Pompom Garland</p>
</div>
<div id="ub31">
<p class="intro" id="i1586a">Weave a magic web around your Christmas tree with these glittery, whimsical, and very original garlands made from knitted I-cords braided together and then dotted with playful pompoms. The cords can be made any length, and the pompoms are removable, making it easy to experiment until you find just the right way to mix and match them.</p>
</div>
<div id="ub2d">
<p class="title2" id="i985a">Yarn</p>
<p class="pgph1" id="i985b">Classic Elite Waterspun (100% felted merino wool; 138 yards / 50 grams): 3 or 4 colors of choice, 
or as indicated below for each Garland 
 <span class="ch_9">Note: 10 yards yarn will make approx 2 feet I-cord.</span></p>
<p class="pgph1" id="i985c">Classic Elite La Gran Mohair (76.5% mohair / 17.5% wool / 6% nylon; 90 yards / 42 grams): 
 1 ball makes approximately 8 solid-color pompoms; also used with gold cord for wrapping braids </p>
<p class="pgph1" style="margin-top:20px;">
Metallic cord (sold at craft/paper stores): 
1 roll each thin gold and silver cord for wrapping braids and making shimmering pompoms</p>
<p class="pgph1" id="i985d"><span class="ch_8">Shown in the following colors:</span>
<span class="ch_9">Note: Numbers beginning with 5 are for Waterspun; 
numbers beginning with 6 are for La Gran.</span></p>
<p class="pgph1"><span class="ch_8">Pink Garland (<a href="#/Chapter1#pinkgarland">opposite</a>)</span>: #5068 madder (brick), 
#5019 India pink, #5089 Julia’s pink (light pink), 
and #5087 bisque (tan); wrapped with #6519 
cameo (light) pink and gold cord.</p>
<p class="pgph1" id="i985e"><span class="ch_8">Green Garland (<a href="#/Chapter1#greengarland">page 23</a>)</span>: #5035 fern green (olive), #5074 pastoral green (yellowish green), #5036 celery (light green); wrapped with #6539 
eucalyptus green and gold cord.</p>
<p class="title2" id="i985f">Needles</p>
<p class="pgph1" id="i985g">Two double-pointed needles (dpn) size US 7 
(4.5 mm) for I-cord</p>
<p class="title2" id="i985h">Notions</p>
<p class="pgph1" id="i985i">10 large safety pins, yarn needle, 2 pieces 
cardboard, each 4" square for pompoms</p>
<p class="title2" id="i985j">Gauge</p>
<p class="pgph1" id="i985k">Not crucial for this project</p>
</div>
<div id="ub16">
<p class="title" id="i985l">Garland</p>
<p class="title3" id="i985m">I-Cords</p>
<p class="pgph2" id="i985n">Using Waterspun, make 3 or 4 15-foot (or 
desired length) 4-stitch I-cords for each strand of garland (see Special Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 177</span>). 
 <span class="ch_5">Note: The finished garland will be slightly shorter than the length of each I-cord.</span></p>
<p class="title3">Pompoms</p>
<p class="pgph2" id="i985o1">Using La Gran and Waterspun, make 15 pompoms, each approximately 4" in diameter, in colors desired (see Special Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 178</span>). Mix and match yarn colors and fibers for <a id="i985o"> interesting effects. Mix in gold and silver cord for extra shine.</a></p><a id="i985o">
<p class="title3" id="i985p">Finishing</p>
</a><p class="pgph2" id="i985q"><a id="i985o">Tie 3 or 4 I-cords together at one end. With a safety pin, attach tied end to a stationary object. Loosely braid I-cords together, securing at 
3-foot intervals with safety pins; tie I-cord ends together. <span class="ch_5"> Note: Instructions for making a 4-strand braid appear on page </span><span class="ch_5"></span></a><span href="#/Chapter4#ElfCaps" title="Link target outside of Sample">171</span>.</p>
<p class="pgph2">
<span class="ch_8">Wrap Garland:</span> Cut 2 strands of La Gran and gold cord each 20 feet in length; holding one strand of each color together, tie ends to one end of the braid; loosely wrap strands around the braid, making a full wrap every 6"; tie strands to the other end of the braid. With remaining strands threaded onto yarn needle, tie ends to one end of the braid; loosely wrap strands around the braid in the opposite direction, making a full wrap every 6"; tie strands to opposite end of the braid. Remove safety pins.</p>
<p class="pgph2" id="i985r">
<span class="ch_8">Attach Pompoms:</span> Attach 13 pompoms, evenly spaced, along the length of the garland by bringing pompom ends around each side of braid and knotting tightly; tie remaining 2 pompoms onto each end of garland. Trim pompom ties.</p>
</div>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="image" id="pinkgarland"><img alt="image" class="full" src="images/0017.jpg"/></p>
<p class="image"><img alt="image" class="full" src="images/0018.jpg"/></p>
<div id="u9065">
<p class="photoCap" id="i1744a">Stocking styles shown 
(left to right): 2, 4, 1</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div id="ud39">
<a id="xmasStockings"></a><p class="intro" id="i192a">	The yarns used for this jolly set of stockings come
from La Lana Wools of Taos, New Mexico, where Sandy
Cushman loves to visit at all times of the year. Here, she has
tried to capture the subtle palette of the landscape in that
part of the country in both color and texture—the hardscrabble
desert, the verdant mountains, and the sunshine
that makes everything brighter and warmer, especially
in winter. The vibrant, earthy colors she has chosen are
repeated in the batik-print lining sewn inside each stocking,
another reminder that holiday celebrations in many parts of
the world—including much of the United States—are often
anything but red and green.</p>
</div>
<div id="ud1e">
<p class="title2" id="i2427a">Finished Measurements</p>
<p class="pgph1" id="i2427b">Approximately 20 (22, 24, 26, 28)" long</p>
<p class="title2" id="i2427c">Yarn</p>
<p class="pgph1" id="i2427d"><span class="ch_8">Yarn 1</span>: La Lana Knitting Worsted (100% wool; 
200 yards / 4 ounces): grape (A), apple green (B), and burgundy (C)  </p>
<p class="pgph1" style="margin-top:0px;"><span class="ch_8">Yarn 2</span>:  La Lana Forever Random Blends 
(wool /  mohair blends; 80 yards / 2 ounces): 
Chaco Canyon (D), yellow brick road (E), faerie 
queen (F), and primavera (G)</p>
<p class="pgph1" id="i2427e"><span class="ch_9">Note: Each Stocking will use</span><span class="ch_9"> 2</span><span class="ch_6">½</span><span class="ch_9"> – </span><span class="ch_9">3 skeins yarn, depending on the size of Stocking; mix and match colors as desired, or work as shown.</span></p>
<p class="title2" id="i2427c2">Needles</p>
<p class="pgph1" id="i2427f">One 16" circular needle size US 9 (5.5 mm)</p>
<p class="pgph1" id="i2427g">One set of five 6" double-pointed needles 
(dpn) size US 9 (5.5 mm)</p>
<p class="pgph1" id="i2427h">One set of five 8" double-pointed needles 
size US 9 (5.5 mm)</p>
<p class="pgph1" id="i2427i">Change needle size if necessary to obtain 
correct gauge.</p>
</div>
<div id="ud35">
<p class="title2" id="i2427j">Notions</p>
<p class="pgph1" id="i2427k">45" wide lining fabric:  <span class="ch_6"> ½</span> yard for Stockings 1 and 2;  <span class="ch_6">  ¾</span> yard for Stockings 3 and 4;  ⅝ yard for Stocking 5; 5 pieces  <span class="ch_6">⅜</span> yard ribbon, each 12" long, for hanger loops; waste yarn</p>
<p class="title2" id="i2427l">Gauge</p>
<p class="pgph1" id="i2427m">Approximately 17  <span class="ch_6">½</span>  sts and 17  <span class="ch_6">½</span>  rows = 4" 
(10 cm) in Slip stitch pattern from Chart using Yarns 1 and 2</p>
<p class="pgph1" id="i2427n">17 sts and 22 rows = 4" (10 cm) in Stockinette stitch (St st) using Yarn 1</p>
<p class="pgph1" id="i2427o"><span class="ch_9"><i>Note: Gauge within stocking is variable due to character of yarn and Stitch patterns.</i></span></p>
<p class="pgph1" id="i2427p"><span class="ch_8">
Shown in the following colors/sizes:</span></p>
<p class="pgph1"><span class="ch_8">Stocking 1:</span> 20" long in Slip st pattern, using B and G for toe, foot, and leg; A and F for Ribbed Top and Heel.</p>
<p class="pgph1" id="i2427q"><span class="ch_8">Stocking 2:</span> 22" long in Stripe pattern, using C, F and G for toe, foot, and leg; A for ribbed Top and Heel.</p>
<p class="pgph1" id="i2427r"><span class="ch_8">Stocking 3:</span> 24" long in Slip st pattern, using A and E for toe, foot, and leg; C for ribbed Top and Heel.</p>
<p class="pgph1" id="i2427s"><span class="ch_8">Stocking 4:</span> 26" long in Stripe pattern, using B and A for toe, foot, and leg; F for ribbed Top and Heel.</p>
<p class="pgph1" id="i2427t"><span class="ch_8">Stocking 5:</span> 28" long in Slip st pattern, using D and C for toe, foot, and leg; A and B for ribbed Top and Heel.</p>
<p class="title2 red" id="i2427u">
Notes</p>
<p class="pgph1" id="i2427v">•These stockings are knit from the toe up to the Ribbed Top, with only a short pause to insert waste yarn where the future heel will be. For ease in working, change from dpn to circ needle when sufficient sts have been increased.</p>
<p class="pgph1" id="i2427w">•When working Slip st pattern, strand yarn loosely behind slipped sts to prevent fabric from puckering.</p>
</div>
<p class="image"><img alt="image" class="full" src="images/chart1.jpg"/></p>
<div id="ude9">
<p class="note2" id="i2428a">• <b>Slip Stitch Pattern</b></p>
<p class="pgph1 wsp" id="i2429a">(multiple of 8 sts; 24-rnd repeat)
<span class="ch_5">Note: See Chart for color placement.</span>
Rnds 1 and 2: * Slip 1, k7; repeat from * around.
Rnds 3 and 4: * K1, slip 1, k5, slip 1; repeat from * around.
Rnds 5 and 6: * Slip 1, k1, slip 1, k3, slip 1, k1; repeat from * around.
Rnds 7 and 8: Repeat Rnds 3 and 4.
Rnds 9 and 10: Repeat Rnds 1 and 2.
Rnds 11 and 12: Knit.
Rnds 13 and 14: K4, * slip 1, K7; repeat to last 4 sts, slip 1, k3.
Rnds 15 and 16: K3, * slip 1, k1, slip 1, k5; repeat to last 5 sts, slip 1, k1, slip 1, k2.
Rnds 17 and 18: K2, * slip 1, k1, slip 1, k1, slip 1, k3; 
repeat to last 6 sts, slip 1, k1, slip 1, k1, slip 1, k1.
Rnds 19 and 20: Repeat Rnds 15 and 16.
Rnds 21 and 22: Repeat Rnds 13 and 14.
Rnds 23 and 24: Knit.
Repeat Rnds 1–24 for Slip st pattern.</p>
<p class="note2" id="i2429o"> 
•<span class="ch_8"></span><span class="ch_8">Make Bobble</span> (mb)</p>
<p class="pgph1" id="i2429p">Knit in [front, back, front] of next st to increase to 3 sts, turn; p3, turn; pass first st over second st, pass second st over third st to decrease to one st.</p>
<p class="pgph1" id="i2429q">
•<span class="ch_8">Bobble BO </span>All Stockings: K1, * mb, pass first st over bobble to BO 1 st, knit bobble st (pulling to tighten bobble); k1, pass bobble st over knit st, [p1, pass last st over st] 2 times, k1, pass last st over st; rep from * around.</p>
</div>
<div id="ude5">
<p class="title" id="i2429r1">General Instructions  <span class="pgph2 black">(All Stockings)</span> </p>
<p class="title3" id="i2429r">
Toe Flap</p>
<p class="pgph2 wsp" id="i2429s">Using 2 dpns and B (C, E, B, D), CO 6 (6, 6, 6, 8) sts; begin St st. Work even for 11 (11, 11, 13, 15) rows; DO NOT TURN. Rotate work to pick up and knit 8 (8, 8, 10, 12) sts along side with Needle 2; rotate to pick up and knit 6 (6, 6, 6, 8) sts across bottom (CO edge) with Needle 3; rotate to pick up and knit 8 (8, 8, 10, 12) sts along other side with Needle 4; then k2 (2, 2, 2, 3) sts from Needle 1 with Needle 4.
Join for working in the rnd.
Knit 1 rnd, distributing sts evenly on four needles—7 (7, 7, 8, 10) sts on each needle; place marker (pm) for beginning of rnd — 28 (28, 28, 32, 40) sts.</p>
<p class="title3" id="i2429u">Toe</p>
<p class="pgph2" id="i2429v">Rnd 1 — Increase rnd:  *  K1, M1, knit to end of Needle 1; knit to 2 sts from end of Needle 2, 
M1, k1; repeat from  *  for Needles 3 and 4.</p>
<p class="pgph2" id="i2429w">Rnd 2: Knit even.</p>
<p class="pgph2" id="i2429x">Repeat Rnds 1 and 2 until there are 12 (13, 14, 15, 16) sts on each needl e —48 (52, 56, 60, 64) sts total.</p>
<p class="title2 gold" id="i2429y">
Slip Stitch Stockings </p>
<p class="title3" id="i2429u2"> Stockings 1 (3, 5)</p>
<p class="pgph2" id="i2429z"><span class="ch_8">Foot:</span> Join G (E, C); begin Slip st pattern and Chart. Repeating Rnds  1 – 24 of stitch pattern, work 35 (47, 59) rnds, ending Rnd 11 (23, 11) 
of stitch pattern.</p>
<p class="pgph2" id="i2429aa"><span class="ch_8">Mark for Heel:</span> Using waste yarn, knit across Needles 1 and 2, turn; purl across Needles 2 and 1.</p>
<p class="pgph2" id="i2430a"><span class="ch_8">Leg:</span> Continuing as established, work Rnd(s)
12–24 (24, 12–24) of Slip st pattern once,
then repeat Rnds 1–24 of Chart 2 (3, 3) times—
61 (73, 85) rnds.</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="image"><img alt="image" class="full" src="images/0021.jpg"/></p>
<div id="u917b">
<p class="photoCap" id="i1227a">Stocking styles shown (left to right): 5, 3</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div id="udf2">
<p class="title2 gold" id="i2430b">Striped Stockings</p>
<p class="title3 wsp" id="i2430b2">Stocking 2: <span class="pgph2 black">3-Color Stripe Sequence</span></p>
<p class="pgph2">Begin St st.</p>
<p class="pgph2" id="i2430d"><span class="ch_8">Foot:</span> Join F; work 3-color Stripe sequence
[2 rnds F, 2 rnds G, 2 rnds C] 6 times, ending last repeat with 1 rnd C—35 rnds.</p>
<p class="pgph2 wsp" id="i2430e"><span class="ch_8">Mark for Heel:</span> Using waste yarn, knit across 
Needles 1 and 2, turn; purl across Needles 2 and 1.</p>
<p class="pgph2" id="i2430f"><span class="ch_8">Leg:</span> Beginning with 1 rnd C, repeat 3-color Stripe sequence 8 more times—49 rnds.</p>
<p class="title3" id="i2430g">Stocking 4: <span class="pgph2 black">2-Color Stripe Sequence</span></p>
<p class="pgph2" id="i2430h">Begin St st.</p>
<p class="pgph2 wsp" id="i2430i"><span class="ch_8">Foot:</span> Join A; work 2-color Stripe sequence
[2 rnds A, 2 rnds B] 11 times, ending last repeat with 1 rnd B—43 rnds.</p>
<p class="pgph2" id="i2430j"><span class="ch_8">Mark for Heel:</span> Using waste yarn, knit across Needles 1 and 2, turn; purl across Needles 2 and 1.</p>
<p class="pgph2" id="i2430k"><span class="ch_8">Leg:</span> Beginning with 1 rnd B, repeat 2-color Stripe sequence 17 more times—69 rnds.</p>
</div>
<div id="udee">
<p class="title2 gold" id="i2430l">All Stockings</p>
<p class="title3" id="i2430m"><span class="ch_8">Ribbed Top</span></p>
<p class="pgph2 wsp" id="i2430n">Join A (A, C, F, A); knit 1 rnd even.
Increase Rnd: * K1, M1, k1, p2; repeat from * around—60 (65, 70, 75, 80) sts.
Ribbing: Work in k3, p2 rib as established until Ribbed Top measures  2 <span class="ch_7">½</span>" , using colors as follows:</p>
<p class="pgph2" id="i2430q"><span class="ch_8">Stocking 1:</span> Join F; alternate 2 rnds A, 2 rnds F for length.</p>
<p class="pgph2" id="i2430r"><span class="ch_8">Stockings 2, 3, and 4:</span> Cont with color established.</p>
<p class="pgph2" id="i2430s"><span class="ch_8">Stocking 5:</span> Join E; alternate 2 rnds A, 2 rnds E for length.</p>
<p class="pgph2" id="i2430t">BO all sts using Bobble BO.</p>
<p class="title3" id="i2430u"><span class="ch_8">Heel</span></p>
<p class="pgph2 wsp" id="i2430v">Carefully remove waste yarn and place sts from top and bottom of heel opening on 2 needles.
Pick up an extra st at the end of each needle—52 (56, 60, 64, 68) sts total. Divide sts evenly onto 4 needles; join A (A, C, F, A).
Rnd 1—Decrease Rnd: * K1, k2tog, knit to end of Needle 1; knit to last 3 sts on Needle 2, ssk, k1; repeat from * for Needles 3 and 4.
Rnd 2: Knit even.
Working heel in same color(s) as for Ribbed Top, repeat Rnds 1 and 2 until there are 4 (4, 4, 5, 5) sts remaining on each needle.
Slip sts from Needle 2 onto Needle 1 and sts from Needle 3 onto Needle 4; graft sts together using Kitchener st (see Special Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 178</span>).
Weave in ends.</p>
<p class="title3" id="i2430aa"><span class="ch_8">Lining</span></p>
<p class="pgph2" id="i2431a">Fold lining fabric in half with RS’s together. Place
completed Stocking (with Ribbed Top folded down
as in photo) on fabric and carefully trace around
Stocking. Cut fabric along tracing, cutting  <span class="ch_7">½</span>" larger 
than tracing along top edge of Stocking. Using   <span class="ch_7">½</span>" 
seam allowance, with RS’s together, sew lining,
leaving top open. Fold top edge  <span class="ch_7">½</span>" down to WS and
press. Fold one 12" long piece of ribbon in half.
Turn lining RS out and stitch lining to inside top
edge of Stocking, securing ribbon in between center
back of Stocking and lining. Tack lining to heel
and Toe.</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="image"><img alt="image" class="full" src="images/0022.jpg"/></p>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div id="uf09">
<a id="menorah"></a><p class="intro" id="i967a">Knitting with wire and beads can be a spiritual experience,
says designer Annie Modesitt. The feeling of slipping
the beads along the wire as well as the color, texture,
and brightness of this medium all invite intro spection and
provide sensations not common to ordinary knitting.
Particularly unusual is the experience of seeing individual
knit stitches backlit by soft candlelight, which is what you’ll
enjoy after knitting these unique votive sleeves. For a menorah,
knit eight in one height and one slightly larger, then
arrange them in a line or semicircle. Or, if you wish, use
them in different numbers and arrangements to add a
unique touch to any celebration of light.</p>
</div>
<div id="ueee">
<p class="title2" id="i578a">Finished Measurements</p>
<p class="pgph1 wsp" id="i578b">2<span class="ch_6">½</span>(3)" high by the circumference of votive holder</p>
<p class="title2" id="i578c">Materials</p>
<p class="pgph1" id="i578d">Artistic Wire (24 gauge): 5 yards for each Votive Sleeve (45 yards total for pieces as shown), 
in color gunmetal; 140 (200) sizes  6– 8 glass beads for each Votive Sleeve ( 1,320 beads for pieces as shown), in various colors</p>
<p class="title2" id="i578e">Needles</p>
<p class="pgph1 wsp" id="i578f">One pair straight aluminum needles size 
US 8 (5 mm)</p>
<p class="title2" id="i578g">Notions</p>
<p class="pgph1" id="i578h">Smooth needle-nose pliers</p>
<p class="title2" id="i578i">Gauge</p>
<p class="pgph1" id="i578j">Not essential</p>
</div>
<div id="uf05">
<p class="title" id="i578k">Votive Sleeve</p>
<p class="pgph2" id="i578l"><span class="ch_5">Note: This sleeve is designed to go around 
the holder, NOT around the actual candle.</span> Instructions below will create a Votive Sleeve that is 2 <span class="ch_7">½</span> (3)" high and as big around as you 
desire, to fit your votive candle holder.</p>
<p class="pgph2" id="i578m"><span class="ch_10">Before You Begin:</span> String 140 (200) beads onto a 5-yard piece of wire in a random fashion.</p>
<p class="pgph2" id="i578n">Using a single strand of beaded wire and a very loose Backward Loop CO method (see Special Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 176</span>), CO 8 (10) sts as follows: CO 1 st, [slip 1 bead to needle, CO 1 st] 7 (9) times.</p>
<p class="pgph2" id="i578o"><span class="ch_10">Set-up Row:</span> P1, [slip 1 bead to needle, p1] 7 (9) times.</p>
<p class="pgph2" id="i578p">Row 1 (RS): K1, [slip 1 bead to needle, k1] 7 (9) times.</p>
<p class="pgph2" id="i578q">Row 2: P1, [slip 1 bead to needle, p1] 7 (9) times.</p>
<p class="pgph2" id="i578r">Rows 3 and 4: Repeat Rows 1 and 2.</p>
<p class="pgph2" id="i578s"><span class="ch_5">Note: For a votive candle holder with straight sides, omit Rows 5 and 6. For a shaped votive candle holder, work Short Rows as follows:</span></p>
<p class="pgph2" id="i578t">Row 5: [K1, slip 1 bead to needle] 4 (5) times, wrap and turn (wrp-t).</p>
<p class="pgph2" id="i578u">Row 6: P1, [sl 1 bead to needle, p1] 3 (4) times.</p>
<p class="pgph2" id="i578v">Repeat Rows 1 – 4 &lt; Rows 1 – 6 &gt;, “blocking” piece as you go by pulling and stretching wire fabric until the sts look even. Continue working until the edge  &lt; shorter edge  &gt; is long enough to reach around the candle holder—16 rows shown in photo; end with a WS row. Cut wire, leaving an 8 (10)" tail.</p>
<p class="title3" id="i578w">Finishing</p>
<p class="pgph2" id="i578x">Join CO and final row with the tail, weaving 
it between CO edge and live sts, one by one, working to the bottom of the sleeve.</p>
<p class="pgph2" id="i578y">Push the wire ends from the front to the back 
of the work. Using needle-nose pliers, twist the wires together, then coil them into a tight circle. Press this firmly (with the broader part of the 
pliers) into the inner edge of the  Votive sleeve.</p>
</div>
<p class="image"><img alt="image" class="full" src="images/0023.jpg"/></p>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div id="u10be">
<a id="lacePhotoMats"></a>
<p class="name dPurple" id="i2239a">VÉRONIK AVERY</p>
<p class="h1 pink" id="i2239b">       Lace Photo Mats</p>
</div>
<div id="u1090">
<p class="intro" id="i1579a">During holiday celebrations families and friends inevitably reminisce about the events of the year just past. These
knitted-lace photograph mats offer a beautiful way to preserve
those special memories.</p>
</div>
<div id="u1079">
<p class="title2" id="i189a">Finished Measurements</p>
<p class="pgph1 wsp" id="i189b"><span class="ch_8">Willow Leaf</span> (opposite, right): 8" by 10"
<span class="ch_8">Lace Cable</span>: 11" by 14"</p>
<p class="title2" id="i189c">Yarn</p>
<p class="pgph1" id="i189d">Lana Gatto Wool Gatto from Needful Yarns (100% wool; 181 yd / 50 grams): #20439 silver 
gray (MC)</p>
<p class="pgph1" id="i189e"><span class="ch_9">Note: Quantities will vary, depending on size 
of mat, but 1 ball is usually sufficient for 2 to  3 mats.</span></p>
<p class="title2" id="i189c2">Needles</p>
<p class="pgph1" id="i189f">One pair straight needles size US 2 (2.75 mm) Change needle size if necessary to obtain 
correct gauge.</p>
<p class="title2" id="i189g">Notions</p>
<p class="pgph1" id="i189h">Yarn needle, cable needle (cn), stitch holders, contrasting waste yarn</p>
<p class="title2" id="i189i">Gauge</p>
<p class="pgph1" id="i189j">24 sts and 48 rows = 4" (10 cm) in Garter stitch</p>
<p class="title2 red" id="i189k">notes</p>
<p class="pgph1 wsp" id="i189l">•<span class="ch_8">  Yarn-Over Short Rows</span></p>
<p class="note2" id="i189m"><span class="ch_10"><b>Corners of Lace Cable Mat</b></span></p>
<p class="pgph2" id="i189n">(RS) Step 1: Work as indicated across row, turn; DO NOT wrap next st.</p>
<p class="pgph2" id="i189o">(WS) Step 2: Begin row with yo (see 
Special Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 180</span>), then work as indicated to end.</p>
<p class="pgph2" id="i189p">(RS) Step 3: Close gap where indicated 
by knitting the yo from the previous row 
together with the next stitch.</p>
</div>
<div id="u10db">
<p class="title" id="i189q">Willow Leaf Mat

</p>
<p class="pgph2" id="i189r">Using waste yarn, CO 12 sts; knit 1 row. 
Break waste yarn. Join MC; purl 1 (WS) row.</p>
<p class="pgph2" id="i189s"><span class="ch_5">Note: The strip will be composed of 4 segments,separated by 3 sections of waste yarn.</span></p>
<p class="pgph2"> <span class="ch_8">* First Width</span>
<br/>Begin Willow Leaf pattern from Chart, Row 1.
<br/>Repeat Rows 1–8 until piece measures approximately 5" from the beginning, or 3" less than inner width of frame, ending Row 8 of stitch pattern.
<br/><span class="ch_8">Establish Corner</span>
<br/>Without breaking MC, knit 2 rows using waste yarn; break waste yarn.
<br/><span class="ch_8">First Length</span>
<br/>Using MC, knit 1 row. Begin Willow Leaf pattern from Chart, Row 1.
<br/>Work as for First Width until piece measures approximately 7" from waste yarn corner, or 3" less than inner length of frame; establish corner using waste yarn.
<br/><span class="ch_8">Second Width and Length</span>
<br/>Repeat from * once, ending second length with Row 8 of stitch pattern; DO NOT work 2 rows waste yarn.
<br/><span class="ch_8">First Corner</span>
<br/>(RS) Continuing with 12 sts of Second Length,
k12; with RS facing, taking care not to twist frame,
pick up and knit 12 sts from CO row—24 sts.
Row 1 (WS): K2tog, k10, place marker (pm),
k10, ssk.
<br/>Row 2: Knit across to 2 sts before marker, ssk,
slip marker (sm), k2tog, knit to end.
<br/>Row 3: Knit.
<br/>Repeat Rows 2 and 3 until 2 sts remain.
<br/>Cut yarn and pull through remaining sts.
<br/><span class="ch_8">Remaining Corners</span>
<br/>With RS facing and garter edge at right, pick up
and knit 12 sts on each side of waste yarn at
next corner—24 sts; remove waste yarn. Work
remaining 3 corners as for First Corner, beginning
with Row 1.
</p>
<p class="title3" id="i189s2">Finishing</p>
<p class="pgph2" id="i189s3">Weave in ends. Pin mat to desired dimensions
(a template can be made from a tracing of
the frame’s backing). Spray or steam and let dry.
Once dry, attach mat to a piece of light cardboard
by tacking lightly with hot glue. Slip
image in through the front opening, and frame
as usual.</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="image" id="greengarland"><img alt="image" class="full" src="images/0025.jpg"/></p>
<p class="image"><img alt="image" class="full" src="images/willowleaf_chart.jpg"/></p>
<div id="u115f">
<p class="pgph2 wsp" id="i190b">
•<span class="ch_8">Willow Leaf Pattern</span> 
(Multiple of 12 sts; 8-row repeat) (See Chart)
Row 1 (RS): *Yo, k1, yo, k2, [k2tog] twice, k2, yo, k2tog, k1; repeat from * across.
Row 2 and all WS rows: *K2, p10; repeat from * across.
Row 3: *Yo, k3, yo, k1, [k2tog] twice, k1, yo, k2tog, k1; repeat from * across.
Row 5: *Yo, k5, yo, [k2tog] twice, yo, k2tog, k1; repeat from * across.
Row 7: *Yo, k3, k2tog, k2, [yo, k2tog] twice, k1; repeat from * across.
Row 8: *K2, p10; repeat from * across.
Repeat Rows 1-8 for Willow Leaf pattern.</p>
</div>
<div id="u1163">
<p class="pgph2 wsp" id="i190c">•<span class="ch_8">Lace Cable Pattern</span>
(Multiple of 13 sts, inc to 16 sts; 12-row repeat) (See Chart)
Row 13: K1, k2tog, yo, 3/3 RC, yo, ssk, [yo] twice, k2—15 sts.
Rows 14 and 20: K2, [k1, p1, k1] into double yo from previous row, purl across to
last 2 sts, k2—16 sts.
Rows 15, 17, 21, and 23: K1, k2tog, yo, ssk, yo, k2, yo, k2tog, yo, ssk, k5.
Rows 16 and 22: K5, purl across to last 2 sts, k2.
Row 18: BO 3, k2, purl across to last 2 sts, k2—13 sts.
Row 19: K1, k2tog, yo, ssk, yo, k2, yo, k2tog, yo, ssk, [yo] twice, k2—15 sts.
Row 24: Repeat Row 18.
Repeat Rows 13–24 for Lace Cable pattern.
Turn Corner of Lace Cable Pattern
Short Rows (Multiple of 13 sts; 12 rows)
Row 25: K1, k2tog, yo, 3/3 RC, yo, ssk, turn.
Rows 26, 28, 30, 32 and 34: Yo, purl across to last 2 sts, k2.
Row 27: K1, k2tog, yo, ssk, yo, k2, yo, k2tog, turn.
Row 29: K1, k2tog, yo, ssk, yo, k2, turn.
Row 31: K1, k2tog, yo, k2, turn.
Row 33: K3, turn.
Row 35: Knit across, closing gaps.
Row 36: Knit.</p>
</div>
<p class="image"><img alt="image" class="full" src="images/lacecable_chart.jpg"/></p>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div id="u116f">
<p class="pgph2" id="i191a">•<span class="ch_8">  First Half-Corner of Lace Cable Pattern</span>
<br/>Short Rows (Multiple of 13 sts; 12-rows)
<br/>(Also see Chart, Rows 1–12)
<br/>Row 1 (RS): K3, turn.
<br/>Rows 2, 4, 6, 8, and 10: Yo, purl to last 2 sts, k2.
<br/>Row 3: K1, k2tog, yo, close gap, k1, turn.
<br/>Row 5: K1, k2tog, yo, ssk, yo, close gap, k1, turn.
<br/>Row 7: K1, k2tog, yo, ssk, yo, k2, close gap, k1, turn.
<br/>Row 9: K1, k2tog, yo, ssk, yo, k2, yo, k2tog, close gap, k1, turn.
<br/>Row 11: K1, k2tog, yo, ssk, yo, k2, yo, k2tog, yo, ssk, close gap, k1.
<br/>Row 12 (WS): K2, purl to last 2 sts, k2.
<br/>These 12 rows complete First Half-Corner—13 sts.</p>
<p class="title" id="i191l">
Lace Cable Mat

</p>
<p class="pgph2" id="i191m">Using waste yarn and Provisional CO method
(see Special Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 178</span>), CO 13 sts.
Break waste yarn.<br/>
Join MC; knit 1 (WS) row.</p>
<p class="pgph2">
<span class="ch_10">First Half-Corner</span>
Begin Lace Cable pattern from Chart, Row 1; work Rows 1–12 once. Pm for beginning of side. </p>
<p class="pgph2"><span class="ch_10">*  Side—Width</span></p>
<p class="pgph2" id="i191p">Work Rows 13  – 24, then repeat these Rows only until piece measures 9  <span class="ch_7">½</span>" from marker, or 
1<span class="ch_7">½</span>" less than inner width of frame.</p>
<p class="pgph2" id="i191q"><span class="ch_10">Turn Corner</span><br/>
Work Rows 2 5– 36 once. Complete Corner by working Rows  1 –12 once; pm.</p>
<p class="pgph2" id="i191r"><span class="ch_10">Side—Length</span><br/>
Work as for Side Width until piece measures 
1 2 <span class="ch_7">½</span>" from marker, or 1  <span class="ch_7">½</span>" less than inner length of frame. Turn Corner by working Rows 2 5 – 36 once, then Rows 1–12 once; pm.</p>
<p class="pgph2 wsp" id="i191s"><span class="ch_10">Remaining 2 Sides</span>
Repeat from  * , turning corners as before.

</p>
<p class="pgph2" id="i191t"><span class="ch_10">Finishing</span></p>
<p class="pgph2" id="i191u">Break yarn, leaving a 12" tail. Remove waste
yarn from CO row; place sts on a second
needle. With WS facing, and yarn threaded on a
yarn needle, graft last row to CO row using
Kitchener st (see Special Techniques, <span href="#/Techniques" title="Link target outside of Sample">page
176</span>). Weave in loose ends.
Block as for Willow Leaf Mat.</p>
</div>
<div class="aside" id="u11c8" style="background-color:#d6edef; padding:1em;">
<a id="santaClaus"></a><p class="name pink" id="i1723a"><b>CREATING—AND PRESERVING—HOLIDAY MEMORIES</b></p>
<p class="intro" id="i1723b">We all grow up with certain holiday traditions and then create some of our own as adults. Here are a few  ideas for knitting-related traditions you might want to try out in your home.</p>
<p class="pgph1"><span class="ch_3">Frame holiday photos</span> using the Lace Photo Mats (pattern starts on <a href="#/Chapter1#lacePhotoMats">page 24</a>).
<br/><br/><span class="ch_3">Knit the same type of garment for each family member.</span> 
For example, you could start with the Candy Cane Hats on <span href="#/Chapter2#candyCane" title="Link target outside of Sample">page 66</span>. Knit a new colorway of the same hat or a completely different style of hat each year.
<br/><br/><span class="ch_3">Knit at least one item to donate to charity every December.</span> Check out local charities or the list of national charities on <span href="#/Chapter3#charitableKnit" title="Link target outside of Sample">page 122</span>.
<br/><br/><span class="ch_3">Host a series of</span> <span class="ch_5">Handknit Holidays</span> <span class="ch_3"> parties.</span> Invite knitting 
friends and family members to your home on a regular basis (once a month throughout the year or maybe just once in the fall—whatever suits your lifestyle). Adding this social element to holiday-knitting plans may help everyone to finish their projects on time. For more holiday-knitting strategies, see <span href="#/Chapter4#strategies" title="Link target outside of Sample">page 167</span>.
<br/><br/><span class="ch_3">Gather with the family for a night of ornament-making.</span> 
Check out the pattern for Knitted and Felted Ornaments on page 10. Also consider making fancy pompoms for the tree
like those featured on the Pompom Garland on <a href="#/Chapter1#pompomGarland">page 16</a>.
Choose a different type of wooly ornament to make each year.
<br/><br/><span class="ch_3">Get together with your knitting group for a holiday bash.</span> Plan on working on a different group project each year. 
To start, check out the Community Afghan on <span href="#/Chapter2#logcabinSocks" title="Link target outside of Sample">page 96</span>.
<br/><br/><span class="ch_3">Schedule at least a few hours (or days) of peaceful knitting</span> before your calendar gets too full of holiday commitments. 
Be sure to write the dates on the calendar so you won’t forget or let chores take precedence.</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div id="u1366">
<a id="hannahTablecloth"></a><p class="name pink" id="i1846a">Annie Modesitt</p>
<p class="h1 lime" id="i1846b">Hannah's Tablecloth</p>
</div>
<div id="u134f">
<p class="intro" id="i864a">Annie Modesitt's grandmother once knitted a tablecloth that became a treasured family heirloom, and it inspired Annie to make one to pass on to her own daughter. But knitting an entire tablecloth can seem insurmountable, especially when a holiday deadline is looming. Annie's clever, timesaving solution utilizes linen napkins, knitted together in a lace pattern and edged with linen yarn.</p>
</div>
<div id="u1333">
<p class="title2" id="i1653a">Finished Measurements</p>
<p class="pgph1" id="i1653b">77" long by 52" wide</p>
<p class="title2" id="i1653c">Yarn</p>
<p class="pgph1" id="i1653d">Louet Euroflax Sportweight Linen (100% linen; 135 yards / 50 grams): 6 skeins pure white</p>
<p class="title2" id="i1653e">Needles</p>
<p class="pgph1" id="i1653f">Two 36" circular (circ) needles size US 4 
(3.5 mm)</p>
<p class="pgph1" id="i1653g">Change needle size if necessary to obtain 
correct gauge.</p>
<p class="title2" id="i1653h">Notions</p>
<p class="pgph1" id="i1653i">Six 21" square linen napkins, stitch holders, stitch markers, darning needles, steam iron, 
and ironing board</p>
<p class="title2" id="i1653j">Gauge</p>
<p class="pgph1" id="i1653k">16 sts and 26 rows = 4" (10 cm) in Stockinette stitch (St st)</p>
<p class="title2 red" id="i1653l">notes</p>
<p class="note2" id="i1653m"> •<span class="ch_8">Double Centered Decrease</span> (dcd) <br/>
Slip 2 sts as if to k2tog to right-hand 
needle, k1, pass 2 slipped sts over knit st.</p>
</div>
<div id="u134a">
<p class="title" id="i1653n">Tablecloth</p>
<p class="title3" id="i1653o">Prepare Napkins</p>
<p class="pgph2" id="i1653p">Using a single strand of yarn, whipstitch 
around the edges of each napkin, making 
42 sts along each edge and 1 st on 
each corner—a total of 172 sts around 
each napkin.</p>
<p class="title3" id="i1653q">Lace Edging</p>
<p class="pgph2" id="i1653r"><span class="ch_10">Napkin Side 1:</span> Knit the first corner st on a 
napkin, place marker (pm), [yo, knit next whipstitch] 42 times, yo, pm—86 sts.</p>
<p class="pgph2" id="i1653s"><span class="ch_10">Napkin Sides 2, 3, and 4:</span> Repeat Side 1—344 sts.</p>
<p class="pgph2" id="i1653t">Continue working in the rnd; begin Lace
patterns, slipping markers as you come to them;
work the corner sts in Chart B, the side sts
in Chart A as follows:
<br/><br/>Rnd 1: * [Yo, k1, yo] in corner st [Rnd 1, Chart B]; k85 [Rnd 1, Chart A]; repeat from * around.
<br/><br/>Rnd 2: * [Yo, k3, yo] in corner st [Rnd 2, chart B], k85 [Rnd 2, Chart A]; repeat from * around.
<br/><br/>Continue from Charts as established until Rnd 12 has been completed, ending Rnds 9 –12 as indicated on Chart.</p>
<p class="pgph2" id="i1653w">* Slip 13 corner sts onto a strand of waste
yarn or stitch holder, slip 85 side sts onto
separate strand of waste yarn or stitch holder;
repeat from * around. Set napkin aside.
Work Lace edging on remaining 5 napkins.</p>
</div>
<div id="u1416">
<p class="title3" id="i1653y">Finishing</p>
<p class="pgph2" id="i1653z"><span class="ch_8">Assemble Napkins</span> (see Diagram)</p>
<p class="pgph2" id="i1653aa"><span class="ch_10">Sides:</span> Slip 85 sts from one napkin side holder
onto one circ needle; slip 85 sts from a second
napkin onto the other circ needle. Arrange
napkins so right sides are facing up and sts that
are on circ needles are next to each other.
Using Kitchener st (see Special Techniques,
<span href="#/Techniques" title="Link target outside of Sample">page 178</span>), graft the two sides of the first 2
napkins together, matching motifs. Leave the
corner stitches on holders. Attach 3 napkins
together to make one strip, then attach the
remaining 3 for a second strip. Lay these two
long pieces side by side and, working on
one 85-st section at a time, graft the sides
together to form a piece 3 napkins long by 2
napkins wide.
<br/><span class="ch_10">Center Circles:</span> Working on one of the two
areas where four napkins come together, using
a triple strand of linen yarn, thread 13 sts from
each of the 4 corners together—a total of 52 sts.
Tie off triple strand, leaving a 1" circle. Repeat
with remaining center corner area.</p>
</div>
<p class="image"><img alt="image" class="full" src="images/0030.jpg"/></p>
<p class="image"><img alt="image" class="full" src="images/charta_side.jpg"/></p>
<p class="noindent"> </p>
<p class="image"><img alt="image" class="full" src="images/chartb_corner.jpg"/></p>
<p class="noindent"> </p>
<p class="image"><img alt="image" class="full" src="images/hannahtableclothassembly.jpg"/></p>
<div id="u1412">
<p class="title3" id="i1654a">Edging</p>
<p class="pgph2" id="i1654b">Slip all stitches, including corner stitches, from the outer edges of the tablecloth onto one circ needle —1058 sts. (Alternatively, you may want to place half the sts on a second circ needle to make working the edging easier.)</p>
<p class="pgph2" id="i1654c">Rnd 1: Beginning at first set of side sts along the shorter edge of the tablecloth,  *  k85 across first side section to where two 13-st corners meet; [slip 6 sts, k1, pass slipped sts over knit st, k13, pass next 6 sts over last knit st—14 sts rem at corners]; k85, k13 corner sts, k85, repeat [ ], k85 sts, repeat [ ], k85, k13 corner sts. Repeat from  *  around— 986 sts remain.</p>
<p class="pgph2" id="i1654d">Rnd 2: Purl.</p>
<p class="pgph2" id="i1654e">Rnds 3 and 4: Knit.</p>
<p class="pgph2" id="i1654f">Rnd 5:  *  P2tog, yo; repeat from  *  around to create Fold line.</p>
<p class="pgph2" id="i1654g">Rnds 6 and 7: Knit.</p>
<p class="pgph2" id="i1654h">Bind off all sts loosely.</p>
<p class="pgph2" id="i1654i"><span class="ch_5">Note: Because linen does not felt, it is necessary to knot ends of yarn together.These 
ends can be separated into thinner strands 
and woven in, but unless the joins are 
knotted, the fabric may come apart with 
repeated washings. After the linen fabric is 
knit, it can be treated the same as the linen 
napkin when washing, drying, and ironing 
the finished tablecloth.</span></p>
<p class="title3" id="i1654i2">Hem</p>
<p class="pgph2" id="i1654j">Steam-press lace edging around circumference. Fold hem at Row 5, creating a picot edge; hand-stitch bound-off edge to WS of work, being careful not to pull the hem too tightly.</p>
<p class="pgph2" id="i1654k">Dampen the entire tablecloth, then steam-press with a very hot iron. Start by steam-press/blocking the knit lace sections, then press the linen napkin areas of the tablecloth.</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="image"><img alt="image" class="full" src="images/0032.jpg"/></p>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div id="u15cf">
<a id="alchemyKitty"></a><p class="intro" id="i1084a">Gina Wilde has been dressing animals since she was small—though she no longer puts blue eye shadow on her Siamese cat, or a black velvet bikini on her English bull 
terrier, as she did as a child. Instead, she’s called upon that innocent fascination with animals to create these fanciful 
feline and canine friends, bold and adorable gifts that 
children can dress and undress all they please.</p>
</div>
<div id="u15b4">
<p class="title2" id="i1107a">Finished Measurements</p>
<p class="pgph1" id="i1107b">Approximately 26" tall</p>
<p class="title2" id="i1107c">Yarn</p>
<p class="pgph1" id="i1107d">Alchemy Lone Star (55% mohair / 45% merino); 135 yards / 100 grams: 3 skeins each in #81f house finch (A) and #31e olive branch (B). 
2 skeins each in #49s punky (C) and #59s rainforest (D).</p>
<p class="pgph1" id="i1107e">Alchemy Synchronicity (50% silk / 50% wool; 110 yards / 50 grams): 1 skein each in #48a 
passion flower (E), #82w Janboy’s sapphire (F).</p>
<p class="pgph1" id="i1107f">Odd scraps of yarn for embroidering facial 
features (both); hair and collar (cat); bandana detail (dog).</p>
<p class="pgph1" id="i1107g"><span class="ch_8">Shown in the following colors:</span></p>
<p class="pgph1" id="i1107h">Kitty (A); Pup (B); Skirt (C); Blouse (E); Overalls (D); Bandana (F)</p>
<p class="title2" id="i1107i">Needles</p>
<p class="pgph1 wsp" id="i1107j">One pair straight needles size US 8 (5 mm)
One 16" circular (circ) needle size US 8 (5 mm)
Two double-pointed needles (dpn) size US 8 (5 mm)
Change needle size if necessary to obtain correct gauge.</p>
<p class="title2" id="i1107l">Notions</p>
<p class="pgph1" id="i1107m">Yarn needle, stitch holders, fiberfill or other stuffing (for bodies), embroidery needle</p>
<p class="title2" id="i1107n">Gauge</p>
<p class="pgph1" id="i1107o">18 sts and 22 rows = 4" (10 cm) in Stockinette stitch (St st) using Reservoir</p>
<p class="pgph1" id="i1107p">20 sts and 26 rows = 4" (10 cm) in St st using Synchronicity</p>
</div>
<div id="u15cb">
<p class="title" id="i1107q2"> Kitty (Pup) Body</p>
<p class="title3" id="i1107q">Arm  <span class="pgph2 black">(make 2)</span></p>
<p class="pgph2" id="i1107r">Using A (B), CO 10 sts.</p>
<p class="pgph2" id="i1107s">Rows 1 and 3: Purl.
<br/>Row 2 (RS): K1,  *  M1, k1; repeat from  *  to end—19 sts.
<br/>Row 4: K8, [M1, k1] 3 times, k8—22 sts.
<br/>Rows  5 –15: Work even in St st.
<br/>Row 16:  *  K2tog, k7, skp; repeat from  *  once —18 sts remain.
<br/>Row 17: Purl.
<br/>Row 18: K1, M1, knit across to last st, M1, k1—20 sts.
<br/>Rows 19 –25: Work even in St st.
<br/>Rows 26– 57: Repeat Rows 1 8 –25; after Row 50—28 sts.
<br/>Rows 58 –70: Work even in St st.
<br/>BO all sts loosely; sew seams.</p>
<p class="title3" id="i1107ad">Leg  <span class="pgph2 black">(make 2)</span></p>
<p class="pgph2" id="i1108a">Using A (B), CO 24 sts.</p>
<p class="pgph2" id="i1108b">Rows 1 and 3: Purl.
<br/>Row 2 (RS): K1,  *  k1-f/b; repeat from  *  to last st, k1—46 sts.
<br/>Row 4: [K1, M1] twice, k18, [M1, k1] 5 times, k18, [M1, k1] twice—54 sts.
<br/>Rows  5 –11: Work even in St st.
<br/>Row 12: K24, k2tog, k1, skp, k25—52 sts remain.
<br/>Rows 13, 15, and 17: Purl.
<br/>Row 14: K23, k2tog, k1, skp, k24—50 sts remain.
<br/>Row 16: K22, k2tog, k1, skp, k23—48 sts remain.
<br/>Row 18: K14, BO next 20 sts, knit to end—28 sts remain.
<br/>Row 19: P14, close gap over BO sts, p14—28 sts.
<br/>Rows 20 –79: Work even in St st.
<br/>BO all sts loosely; sew leg and foot seam.</p>
</div>
<p class="image"><img alt="image" class="full" src="images/0034.jpg"/></p>
<div id="u16b4">
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="title3" id="i1109a">Body and Head</p>
<p class="pgph2 wsp" id="i1109b">Using A (B), CO 56 sts.
<span class="ch_8">Shape Body:</span> Rows 1, 3, 5, 7, and 9: Purl.
Row 2 (RS): K13, [M1, k1] 3 times, k12, M1, k13, [M1, k1] 3 times, k12—63 sts.
Row 4: K31, M1, k1, M1, k31—65 sts.
Row 6: K31, M1, k3, M1, k31—67 sts.
Row 8: K31, M1, k5, M1, k31—69 sts.
Rows 10–25: Work even in St st.
Row 26: K13, k2tog, k1, skp, k33, k2tog, k1, skp, k13—65 sts remain.
Rows 27–31: Work even in St st.
Row 32: K12, k2tog, k1, skp, k31, k2tog, k1, skp, k12—61 sts remain.
Rows 33 –37: Work even in St st.
Row 38: K11, k2tog, k1, skp, k29, k2tog, k1, skp, k11—57.
Rows 39–43: Work even in St st.
Row 44: K10, k2tog, k1, skp, k11, skp, k1, k2tog, k11, k2tog, k1, skp, k10—51 sts remain.
Rows 45, 47, and 49: Purl.
Row 46: K23, skp, k1, k2tog, k23—49 sts remain.
Row 48: K22, skp, k1, k2tog, k22—47 sts remain.
Row 50: K9, k2tog, k1, skp, k7, skp, k1, k2tog, k7, k2tog, k1, skp, k9—41 sts remain for head.
Row 51: Purl.
<span class="ch_8">Shape Head:</span> Row 52: K20, M1, k1, M1, k20—43 sts.
Rows 53–57: Work even in St st.
Row 58: K1, [M1, k20, M1, k1] twice—47 sts.
Rows 59–61: Work even in St st.
Row 62: K23, M1, k1, M1, k23—49 sts.
Row 63: P24, M1, p1, M1, p24—51 sts.
Row 64: K1, [M1, k24, M1, k1] twice—55 sts.
Row 65: P27, M1, p1, M1, p27—57 sts.
Rows 66–67: Work even in St st.
Row 68: K1, [skp, k23, k2tog, k1] twice—53 sts remain.
Rows 69–71: Work even in St st.
Row 72: K1, [skp, k21, k2tog, k1] twice—49 sts remain.
Rows 73, 75, 77, 79, and 81: Purl.
Row 74: K1, skp, k5, skp, k29, k2tog, k5, k2tog, k1—45 sts remain.
Row 76: K1, [skp, k17, k2tog, k1] twice—41 sts remain.
Row 78: K1, skp, k3, skp, k25, k2tog, k3, k2tog, k1—37 sts remain.
Row 80: K1, [skp, k13, k2tog, k1] twice—33 sts remain.
Row 82: [K1, skp] twice, k8, k2tog, k1, skp, k8 [k2tog, k1] twice—27 sts remain.
Row 83: Purl.
BO all sts loosely.</p>
</div>
<div id="u16bc">
<p class="title3" id="i1110v">Kitty Ears</p>
<p class="pgph2 wsp" id="i1110w"><span class="ch_8">Outer Ear</span> (make 2)
Using A, CO 9 sts.
Rows 1–4: Work even in St st.
Row 5: Decrease 1 st each side—7 sts remain.
Rows 6 and 7: Work even in St st.
Rows 8–13: Repeat Rows 5–7 twice—3 sts remain.
Row 14: Work 3 sts together—1 st remains.
Fasten off.
<span class="ch_8">Inner Ear</span> (make 2)
Using scrap yarn, CO 7 sts.
Rows 1–3: Work even in St st.
Row 4: Decrease 1 st each side—5 sts remain.
Rows 5 and 6: Work even in St st.
Row 7: Repeat Row 4—3 sts remain.
Row 8: Work 3 sts together—1 st remains.
Fasten off.
With WS facing (RS’s held together), sew inner ear to outer ear. Turn RS out and sew bottom seam.</p>
<p class="title3" id="i1111h">Pup Ears</p>
<p class="pgph2" id="i1111i"><span class="ch_8">Outer Ear</span> (make 2)
<br/>Using B, * CO 2 sts; begin St st.
<br/>(WS) Purl 1 row.
<br/><span class="ch_8">Shape Ear:</span> (RS) Increase 1 st each side this row, then every other row 6 times—16 sts.
<br/>Work even for 5 rows.
<br/>Decrease 1 st each side this row, then every 4 rows 2 times—10 sts remain.
<br/>Work 1 row even; pm each side this row. *
<br/>Decrease 1 st at end of this row, then every other row 3 times—6 sts remain.
<br/>Work 1 row even; BO all sts.
<br/><span class="ch_8">Inner Ear</span> (make 2)
<br/>Using scrap yarn, work from * to * of outer Ear.
<br/>Next Row: Decreasing 1 st at each side, BO all sts. Sew pieces together as for Kitty Ears.</p>
<p class="title3" id="i1111t">Kitty Tail</p>
<p class="pgph2" id="i1111u">Using A, CO 15 sts; begin St st. Work even
until piece measures 9" from beginning, ending
with a WS row.<br/>
<span class="ch_8">Shape Tail:</span> Decrease 1 st each side this row,
then every 4th row until 7 sts remain.
BO all sts loosely; sew seam, including tip of tail.</p>
</div>
<div id="u16c0">
<p class="title3" id="i1112a">Kitty Hair</p>
<p class="pgph2" id="i1112b"><span class="ch_5">Note: The number of sts needed will depend on
the gauge of the yarn being used.</span><br/>
Using scrap yarn, CO and work a Garter st rectangle
approximately 1" by 5"; BO all sts loosely.</p>
<p class="title3" id="i1112d">Finishing</p>
<p class="pgph2" id="i1112e"><span class="ch_5">Stuffing Note: Use small pieces (about
an inch or two in size) as the smaller pieces
will blend together nicely, preventing a
lumpy appearance.</span><br/>
<span class="ch_8">Stuff Legs:</span> Be sure foot is firmly filled; as you
move up the Leg with stuffing, ease the amount
as you draw near the top. This will make for
easier sewing together, as well as a finished
piece that sits cooperatively. Sew top of
Leg closed.<br/>
<span class="ch_8">Stuff Arms:</span> Fill hands firmly, and ease amount
of stuffing as for Legs toward the top of the
Arm. Sew top of Arm closed.<br/>
<span class="ch_8">Stuff Body:</span> Join Body and head at back seam.
Stuff this piece, using the opening at the lower
edge of the Body; be sure to stuff face with
attention to shaping—fill out the Nose, ease a
bit through the Neck, etc.<br/>
<span class="ch_8">Assemble Kitty/Pup:</span> Insert top of Legs at
lower edge of Body; sew in place. Sew Arms in
place. Fold Ears in half lengthwise; sew in place.
Center hair on top of head, between Ears, and
sew in place. If desired, make a French knot for belly button.<br/>
<span class="ch_8">Embroider Face:</span> The face is the personality of
your creation, so plan on devoting some time to
embroidering it—this isn’t a rush job. Look at
the photo for ideas.<br/>
Be sure to make simple, clear stitches. If you
don’t get exactly the look you desire in your first
attempt at embroidering, carefully remove the
stitches and try again. Be sure to give the open
eye of the cat a dark pupil, a definitive feature
that makes the cat come alive.</p>
<p class="title" id="i1112m">
Clothing and Accessories</p>
<p class="title3" id="i1112n">Holiday Collar</p>
<p class="pgph2" id="i1112o">Using scrap yarn and dpn, CO 7 sts; begin I-cord (see Special Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 177</span>). Work Collar to length desired (it should fit snugly around Kitty’s neck); BO all sts.</p>
</div>
<div id="u17a9">
<p class="title3 black" id="i1112p">Berries</p>
<p class="pgph2" id="i1112q">Using scrap yarn and dpn, CO 3 sts;
begin I-cord.<br/>
Work until piece measures <span class="ch_7">½</span>" from the beginning;
do not BO. Join live sts to CO end, rolling
into a ball.</p>
<p class="title3 black" id="i1113a">Leaves</p>
<p class="pgph2 wsp" id="i1113b">Using scrap yarn, CO 2 sts.
Row 1: Purl.
Row 2: K1-f/b of first and last st—4 sts.
Rows 3 and 4: Repeat Rows 1 and 2—6 sts.
Row 5: Purl.
Row 6: Ssk, knit across to last 2 sts, k2tog—4 sts remain.
Rows 7 and 8: Repeat Rows 5 and 6—2 sts remain.
Row 9: P2tog; fasten off.
Sew berries and leaves in place, and fasten
Collar at Back of neck.</p>
<p class="title3" id="i1113k">Skirt</p>
<p class="pgph2" id="i1113k2">Using C and circular needle, CO 140 sts.
Join for working in the round, being careful not to twist sts; place marker (pm) for beginning
of rnd. Begin Garter st.
Work even for 6 rnds in Garter st (purl 1 rnd,
knit 1 rnd). Change to St st; work even for 7".<br/>
<span class="ch_10">Shape Skirt:</span> * K2tog; repeat from * around—
70 sts remain.<br/>
Work even for 6 rnds in Garter st; BO remaining
sts loosely.<br/>
Straps (make 2): Using C, CO 40 sts. Knit even
for 2 rows; BO all sts loosely.<br/>
Weave in ends. Sew Straps to Skirt, adjusting
length as necessary to fit Kitty.</p>
<p class="title3 wsp" id="i1113q">Blouse 
<span class="black">Front</span></p>
<p class="pgph2 wsp" id="i1113r">Using E, CO 40 sts; begin Seed st.
(RS) Work even for 2 rows, ending with a WS row.
(RS) Change to St st; work even for 6 rows, pm at each end of last (WS) row.
Work even for 16 more rows, ending with a WS row.</p>
</div>
<div id="u17ad">
<p class="title3 black" id="i1114a"><span class="ch_8">Left Front/Back</span></p>
<p class="pgph2" id="i1114b">Shape Shoulders and Neck: (RS) K9, turn.<br/>
Continuing in St st, working on these 9 sts only,
work even for 10 rows for shoulder strap; place
sts on holder.<br/>
<span class="ch_10">Right Front/Back</span><br/>
(RS) Rejoin yarn at neck edge; BO next 22 sts
for neck, knit to end—9 sts remain. Work as
for left Front/Back for 10 rows; do not place
sts on holder.<br/>
<span class="ch_10">Back</span><br/>
(WS) P9; CO 22 sts for Back neck; purl across
sts on holder for left Front—40 sts.
Continuing in St st, work even for 16 rows; pm
at each end of last (WS) row.<br/>
Work even for 6 rows. Change to Seed st; work
even for 2 rows. BO all sts loosely in pattern.<br/>
<span class="ch_10">Sleeves</span><br/>
With RS facing, pick up and knit 46 sts between
markers. Begin Seed st; work even for 2 rows.
BO all sts loosely in pattern. Sew side and
sleeve seams.<br/>
<span class="ch_10">Finishing</span><br/>
Neckband: With RS facing, pick up and knit
65 sts evenly around neck opening. Join for
working in the rnd; pm for beginning of rnd.
Begin Seed st; work 2 rnds even. BO all sts
loosely in pattern.</p>
<p class="title3" id="i1114m">Overalls </p>
<p class="title3"><span class="black">Right Leg</span></p>
<p class="pgph2 wsp" id="i1114o">Using D, * CO 40 sts; begin Garter st.
Work even for 6 rows.
Change to St st; work even for 6 rows.
<span class="ch_10">Shape Leg:</span> Increase 1 st each side this row, then every 6 rows 6 times—54 sts.
Work even for 4 rows; pm each end of this row.
Continuing in St st, work 18 rows.*
Next Row: P9 and place on holder for Back
Bib; BO 29 sts; p16 and place on holder for
Front Bib.
<span class="ch_10">Left Leg</span>
Work from * to * of right Leg.
Next Row: P16 sts and place on holder for
Front Bib; BO 29 sts; p9 and place on holder for Back Bib.
<span class="ch_10">Pocket Lining</span>
Using D, CO 22 sts; begin St st. (RS) Work even for 17 rows; place sts on holder.</p>
</div>
<div id="u17b1">
<p class="pgph2 wsp" id="i1116h"><span class="ch_8">Front Bib</span>
With RS facing, join yarn at Front Bib sts of left Leg; k16 Front Bib sts from left 
Leg holder, then knit across 16 Front Bib sts from right Leg holder—32 sts.
Knit even for 4 rows.
Next Row: (WS) Decrease 1 st each side—30 sts remain.
Row 1: Knit.
Row 2: K3, p24, k3.
Rows 3–18: Repeat Rows 1 and 2.
Row 19: K4, BO 22 sts, k4.
Row 20: K3, p1, purl across 22 sts of Pocket Lining, p1, k3—30 sts.
Rows 21 and 22: Repeat Rows 1 and 2.
Rows 23–28: Knit.
BO all sts.
<span class="ch_8">Back Bib</span>
With RS facing, join yarn at Back bib sts of right
Leg; k9 Back Bib sts from right Leg holder, then knit across 9 Back Bib sts of left Leg holder—18 sts.
Rows 1, 3, and 5: K3, purl across to last 3 sts, k3.
Rows 2 and 4: Knit.
Row 6: K3, skp, purl to last 5 sts, k2tog, k3—16 sts remain.
Rows 7–23: Repeat Rows 1–6 twice, then
Rows 1–5 once more—12 sts remain.
Row 24: K3, place sts on holder; BO 6 center sts; k3.
<span class="ch_8">Straps</span>
Continuing on last 3 sts, begin I-cord (see
Special Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 175</span>), 
making a strap that, when slightly stretched, fits over shoulder to attach to Front Bib.
K3tog, fasten off.
Join yarn to remaining 3 sts on holder; work as first Strap.</p>
</div>
<div id="u17b2">
<p class="pgph2" id="i11162h"><span class="ch_8">Finishing</span><br/>
Join Leg seams from lower edge to markers;
join center Front and Back seams.<br/>
Sew Pocket Lining to WS of Bib; using backstitch,
make a divide in pocket (see photo).<br/>
Sew straps to Front Bib.<br/>
<span class="ch_8">Side Panels</span> (make 2)<br/>
With RS facing, pick up and knit 20 sts evenly
between Front and Back Bibs; begin St st.<br/>
Work even for 14 rows; BO all sts.<br/>
Sew side seams, joining to sides of Front and
Back bibs.<br/>
Repeat for other side.<br/>
Weave in ends.</p>
<p class="title3" id="i11163h">Snowflake Bandana</p>
<p class="pgph2 wsp" id="i11164h">Using F, CO 2 sts; begin Garter st.
Knit 1 row.
<span class="ch_8">Shape Bandana:</span> Increase 1 st each side this row, then every other row 28 times—60 sts.
Knit 1 row even.
<span class="ch_8">Ties:</span> CO 10 sts; knit 10 CO sts, k60, CO 10 sts—80 sts.
Knit 1 row; BO all sts. Weave in ends.
Embroider snowflake motif with contrasting scrap yarn (see photo).</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="image"><img alt="image" class="full" src="images/0037.jpg"/></p>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div id="u18ed">
<a id="hanukkah"></a><p class="name red" id="i785a">Suzanne Atkinson</p>
<p class="h1 dPurple" id="i785a2">Menorah Pillow</p>
</div>
<div id="u1904">
<p class="intro" id="i1737a">Hanukkah is the Jewish Festival of Lights, and its most
recognizable symbol is the menorah—the nine-branched
candelabra on which candles are lit and blessed, one by one,
over the eight days of celebration. Contemporary menorahs
are made from all kinds of materials—the elegant one on the
front of this pillow is crafted from knitted cables in a luxu­rious 
<span class="wsn1">blend of wool and cashmere, inviting rest for a holiday­weary</span>
head. The back of the pillow is worked in two pieces in
double-moss stitch, then closed with knotted I-cord buttons.
The menorah motif here was inspired by a candlestick design
in <i>Charted Knitting Designs</i> by Barbara G. Walker.</p>
</div>
<div id="u18d6">
<p class="title2" id="i2469a">Finished Measurements</p>
<p class="pgph1" id="i2469b">To fit 14" square pillow form snugly</p>
<p class="title2" id="i2469c">Materials</p>
<p class="pgph1" id="i2469d">Debbie Bliss Cashmerino Aran (55% merino wool / 33% microfiber / 12% cashmere; 
100 yards / 50 grams): 5 balls #300102 eggshell</p>
<p class="title2" id="i2469e">Needles</p>
<p class="pgph1" id="i2469f">One pair straight needles size US 7 (4.5 mm)</p>
<p class="pgph1" id="i2469g">Two 24" (60 cm) circular (circ) needles 
size US 6 (4 mm)</p>
<p class="pgph1" id="i2469h">One set of double-pointed needles (dpn) 
size US 7 (4.5 mm)</p>
<p class="pgph1" id="i2469i">Change needle size if necessary to obtain 
correct gauge.</p>
<p class="title2" id="i2469j">Notions</p>
<p class="pgph1" id="i2469k">14" square pillow form, cable needle (cn), 
crochet hook size G/6 (4 mm), stitch holder, waste yarn, sewing needle, matching 
colored thread, yarn needle</p>
<p class="title2" id="i2469l">Gauge</p>
<p class="pgph1" id="i2469m">21 sts and 32 rows = 4" (10 cm) using larger needles in Double-moss st after blocking</p>
</div>
<div id="u1932">
<p class="title2 red" id="i2469n">Notes</p>
<p class="pgph1 wsp" id="i2469o">•<span class="ch_8">Double-Moss Stitch</span>
(Multiple of 4 sts; 4-row repeat)
Rows 1 and 2: * P2, k2; repeat from * across.
Rows 3 and 4: * K2, p2; repeat from * across.
Repeat Rows 1–4 for Double-moss st. </p>
<p class="title" id="i2469o2">
Pillow</p>
<p class="title3" id="i2469s">Front</p>
<p class="pgph2 wsp" id="i2469t">Using larger needles and Provisional Chain
CO (see Special Techniques, page 176),
CO 76 sts; begin Double-moss st for lower border as follows:
Row 1 (RS): K3, * p2, k2; repeat from * to last 3 sts, end k3.
Row 2: P3, * k2, p2; repeat from * to last 3 sts, end p3.
Row 3: P3, * k2, p2; repeat from * to last 3 sts, end p3.
Row 4: K3, * p2, k2; repeat from * to last 3 sts, end k3.
Repeat Rows 1–4 once more.
Set-up Rows (RS): K3, p2, k2, p13, M1, k18, M1, k18, M1, p13, k2, p2, k3—79 sts.
Next Row: P3, k2, p2, place marker (pm); k14, p37, k14, pm; p2, k2, p3.
Maintaining 7 st border in Double-moss st on each side, begin Chart on center 65 sts. 
Work 97 rows of Chart.  <span class="ch_5">Note: There is one decrease 
on the last row of the Chart</span>—76 sts remain.
<span class="ch_8">Top Border:</span> Work even for 8 rows in Double-moss st as for lower border. DO NOT BO.
Break off yarn; leave sts on a spare thread.</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="image"><img alt="image" class="full" src="images/0039.jpg"/></p>
<p class="noindent"> </p>
<p class="image"><img alt="image" class="full" src="images/0040.jpg"/></p>
<p class="noindent"> </p>
<p class="image"><img alt="image" class="full" src="images/0041.jpg"/></p>
<p class="noindent"> </p>
<p class="image"><img alt="image" class="full" src="images/key_menorah.jpg"/></p>
<div id="u19b8">
<p class="title3" id="i2470b">Back</p>
<p class="pgph2" id="i2470c"><span class="ch_8">Lower Piece:</span> Using larger needles and Provisional Chain CO (see Special Techniques, page 176), CO 76 sts; begin Double-moss st 
as for lower Front border.
Work even as established for 64 rows; 
BO all sts.</p>
<p class="pgph2" id="i2470d"><span class="ch_8">Upper Piece:</span> Using larger needles and Provisional Chain CO, CO 76 sts; begin 
Double-moss st as for lower Back.</p>
<p class="pgph2" id="i2470e">Work even for 8 rows.</p>
<p class="pgph2" id="i2470f">(RS) Buttonhole Row: Continuing in pattern 
established, work 11 sts; [BO 3 sts, work 14 sts] 3 times, BO 3 sts; work to end (11 sts).</p>
<p class="pgph2" id="i2470g">Next Row: Work 11 sts; CO 3 sts over BO sts 
of previous row by working 3 firm Backward Loop CO’s (see Special Techniques, page 174); 
work to end. Work even as established for 64 rows. Do not BO. Break off yarn; leave sts on spare thread.</p>
<p class="title3" id="i2470h">Finishing</p>
<p class="pgph2" id="i2470i">Block Front piece to measure 14 <span class="ch_7">½</span>" square, 
and each Back piece to measure 14 <span class="ch_7">½</span>" x  8 <span class="ch_7"> ¼</span>" by pinning to measurements with rustproof pins 
and misting well with cool water. Allow to air-dry. Place Upper Back piece over Lower Back 
piece, overlapping 16 rows at center; baste overlapped side edges together with sewing needle and thread.</p>
<p class="title3 black" id="i2470j">Lower Seam</p>
<p class="pgph2" id="i2470k">Carefully remove Provisional Chain CO from
lower edge of Front by unpicking the first couple
of sts of the crocheted chain, then unzipping
the remainder. Place these sts on smaller circ
needle. Do likewise for the Lower Back piece,
placing the sts on second smaller circ needle.
With WS of pillow pieces together and Front
facing, using a dpn and working yarn, CO 3 sts
onto one of the circ needles.</p>
<p class="pgph2">Work 3-needle I-cord BO as follows:
<br/>
* K2, slip 1, k2tog (one st each from Front and
Back needles), psso to BO 1 st; return sts to
left-hand needle. Repeat from * across, until
one st remains on each circ needle; work corner
as follows:<br/>
Work one row of unattached I-cord.<br/>
Work 3-needle I-cord BO for last pair of sts.<br/>
Work one row of unattached I-cord.<br/>
Place 3 sts on a holder while preparing sts for
side seam.</p>
</div>
<div id="u19bc">
<p class="title3 black" id="i2470r">Side Seam</p>
<p class="pgph2" id="i2470s">With RS facing, working one st in from edge using smaller circ needle and a new length of working yarn, pick up and knit one st for each row along the right-hand side of Front. Break off new strand of yarn.</p>
<p class="pgph2" id="i2470t1">With RS facing, using second smaller circ 
needle and a new length of working yarn, pick up and knit one st for each row (the same 
number of sts as picked up along Front) along the left-hand side of Back, being sure to work through both layers where pieces overlap. </p>
<p class="pgph2" id="i2470t">
Break off yarn.</p>
<p class="pgph2" id="i2470u">With WS of pillow pieces together, continue working 3-needle I-cord BO (for side seam) until one st remains on each circ needle; work corner as before.</p>
<p class="title3 black" id="i2470v">Upper Seam</p>
<p class="pgph2" id="i2470w">Remove sts from spare thread and place sts from top edge of Front on one circ needle and sts from Back on second circ needle. Continue to work 3-needle I-cord BO, until one st remains on each circ needle; work corner as before.</p>
<p class="title3 black" id="i2470x">Side Seam</p>
<p class="pgph2" id="i2470y">Work second side seam as for first; work corner as before. Break off yarn, leaving several inches. Do not BO. With yarn needle threaded with 
remaining yarn, neatly weave end of I-cord to beginning of I-cord. Remove basting thread 
from Back.</p>
<p class="title3 black" id="i2470z">I-Cord Knot Buttons  <span class="pgph2">(Make 4)</span></p>
<p class="pgph2" id="i2470aa">Using dpn, CO 4 sts. Work I-cord for 6" (see Special Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 175</span>). Tie into a large knot twice and tack ends onto WS of button. Sew buttons in place on lower piece of  b ack opposite buttonholes. Weave in all ends.</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="image"><img alt="image" class="full" src="images/0042.jpg"/></p>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div id="u1b42">
<a id="floralTreeSkirt"></a><p class="intro" id="i2035a">The inspiration for these coordinating pieces came from
the beautiful handspun, kettle-dyed Uruguayan yarn used to
knit them. The subtle variations of color, the bright floral
motifs, and the pewter-button closure meld to form a
multi cultural look—part Turkish kilim rug, part Victorian
tapestry, and part Icelandic cardigan. Leftover yarn from the
skirt creates a fun-to-knit striped stocking, generously sized
to hold plenty of goodies.</p>
</div>
<div id="u1b10">
<p class="title2" id="i2375a">Finished Measurements</p>
<p class="pgph1" id="i2375b"><span class="ch_8">Tree Skirt</span>: approximately 60" diameter</p>
<p class="pgph1"><span class="ch_8">Stocking</span>: 7" wide opening; 16 <span class="ch_6"> ¾</span> " from top to  back of heel; 11  <span class="ch_6">½</span> " from back of heel to tip of toe</p>
<p class="title2" id="i2375c">Yarn</p>
<p class="pgph1 wsp" id="i2375d">Manos del Uruguay (100% handspun 
kettle-dyed wool; 135 yards / 100 grams): 
5 skeins #66 red, 4 skeins #65 gold, 3 skeins 
#108 charcoal, 1 skein each #69 coral, 
#37 brown, #52 cream, #55 dark green, 
#67 chartreuse, #33 seafoam, #50 lilac, 
#115 ruby, #32 orchid, #67 medium green</p>
<p class="title2" id="i2375e">Needles</p>
<p class="pgph1" id="i2375f">One pair straight needles size US 8 (5 mm)</p>
<p class="pgph1" id="i2375g">One set of double-pointed needles (dpn) size 
US 8 (5 mm)</p>
<p class="pgph1" id="i2375h">Change needle size if necessary to obtain 
correct gauge.</p>
<p class="title2" id="i2375i">Notions</p>
<p class="pgph1" id="i2375j">Stitch markers, eight approximately   <span class="ch_6"> ¾</span> " buttons, yarn needle</p>
<p class="pgph1" id="i2375k">Optional: Crochet hook size H/8 (5 mm) for Stocking hanger</p>
<p class="title2" id="i2375l">Gauge</p>
<p class="pgph1 wsp" id="i2375m"><span class="ch_8">Tree Skirt</span>: 16 sts and 21 rows = 4" (10 cm) in 
Colorwork pattern from Chart
 <span class="ch_8">Stocking</span>: 15 sts and 20 rows = 4" (10 cm) in 
 Stockinette stitch (St st)</p>
</div>
<div id="u1b27">
<p class="title2 red" id="i2375n">Notes</p>
<p class="pgph1" id="i2375o">•	The Tree Skirt is knit in 4 separate panels. Each panel consists of 3 pie-shaped 
segments. Although the  Chart appears to 
be three separate  Charts, it is knit as 
one. Skip the spaces between each 
segment and continue to the next st in 
the adjacent segment.</p>
<p class="pgph1" id="i2375p">•	Carry colors loosely across back of work 
for border (stranded method; see Special Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 179</span>); use separate balls of yarn for each color (intarsia method; 
see Special Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 177</span>) for the floral section.</p>
<p class="pgph1" id="i2375q">•	Slip markers as you come to them.</p>
<p class="title" id="i2375r">
Tree Skirt</p>
<p class="title3" id="i2375s">3-Segment Panel (make 4)</p>
<p class="pgph2 wsp" id="i2375t">Using red, CO 28 sts (counts as Row 1).
Rows 2 and 4: Knit.
Row 3 (RS): K9 (Segment 1), pm, k9 (Segment 2), pm, k10 (Segment 3).
Row 5—Increase Row: * K1, M1, work to marker following Chart; repeat from * twice—31 sts.</p>
<p class="pgph2" id="i2375x">Continue following Chart, working increases
into pattern every second and fourth rows as
indicated on Chart—48 sts each in Segments
1 and 2, 49 sts in Segment 3; 145 total.
When Chart is complete, BO all sts using red.</p>
</div>
<p class="image"><img alt="image" class="full" src="images/0043.jpg"/></p>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div id="u1c10">
<a id="Myrna"></a>
<p class="title3" id="i2375z">Lace Border</p>
<p class="pgph2" id="i2375aa">Using red, CO 10 sts; begin Lace Border Chart. Work Rows  1 – 10 of Chart 76 times, or 
until piece is long enough to go around the 
entire outer circumference of the skirt. 
Lightly steam-block.</p>
<p class="title3" id="i2375ab">Finishing</p>
<p class="pgph2" id="i2375ac">Weave in loose ends on WS of each panel.
Lightly steam-block each panel.<br/>
<span class="ch_8">Assemble Tree Skirt:</span> Lay 4 panels out into a
circle; patterns should match up at the edges.
With RS facing, sew 2 panels together, making
sure to match up patterns at seams. Sew third
panel to this piece; then sew fourth panel in
same manner, leaving the 2 sides of the Tree
Skirt open. On the open straight edges, work
bands as follows:<br/>
<span class="ch_8">Button Band:</span> With RS facing, beginning at
inner edge of Skirt using red, pick up and knit
86 sts along straight edge. Work even for 5
rows in Seed st. BO all sts loosely in pattern.<br/>
<span class="ch_8">Buttonhole Band:</span> On opposite straight edge,
with RS facing, beginning at outer edge of Skirt
using red, pick up and knit 86 sts. Work even
for 2 rows in Seed st.<br/>
<span class="ch_8">Buttonhole Row (WS):</span> Work 3 sts in Seed st;
[BO 2 sts, work 9 sts in Seed st] 7 times, BO 2
sts, work 4 sts in Seed st. Next Row (RS):
Continuing in Seed st, CO 2 sts over BO sts of
previous row.<br/>
Work 1 more row in Seed st; BO all sts.<br/>
<span class="ch_8">Attach Lace Border:</span> With RS facing, sew
border onto the outer edge of the Skirt.
Lightly steam-block border and panel seams.
Sew buttons opposite buttonholes.</p>
</div>
<p class="noindent"> </p>
<div class="aside" id="u1c15" style="background-color:#c21445; padding:2em;">
<p class="name cream" id="i1997a"><b>The Origin of 
Christmas Stockings</b></p>
<p class="intro cream" id="i1997b">Long before he became immortalized as Saint Nick—so the legend goes—the kindly fourth-century bishop Nicholas of Myra made a practice of slipping into the houses of his poor parishioners and leaving gifts in their shoes. In one especially famous instance, Nicholas’s heart went out to three sisters who wished to marry, but whose family was too poor to provide dowries. When the first two sisters were married, he secretly left a bag of gold in their shoes. But when the third sister was ready to marry, concerned that the family was on the lookout for him, he climbed onto the roof and dropped a bag of gold down the chimney. The bag landed in a stocking hung by the fireplace to dry—and ever since, in many cultures, stockings have been hung by fireplaces to be magically filled with gifts on Christmas Eve.</p>
<p class="intro cream" id="i1997c">Still, there are some European and Latin American countries where shoes rather than stockings are laid out by the fire to receive treats. Sometimes, these shoes are filled with hay for Santa’s reindeer or greens for the Three Kings’ camels. In Scandinavia, families often make a point of placing all their shoes side by side on Christmas Eve, in hope of 
ensuring family harmony for the coming year.</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="image"><img alt="image" class="full" src="images/0045.jpg"/></p>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div id="u1c08">
<p class="title" id="i2377e">Stocking</p>
<p class="title3" id="i2377f">Leg</p>
<p class="pgph2" id="i2377g">Using red, CO 54 sts. Working back and forth,
begin Leg Chart. When Chart is complete,
break off coral and begin heel.</p>
<p class="title3" id="i2377g2">Heel Flap</p>
<p class="pgph2" id="i2377g3"><span class="ch_5">Note: Heel is worked on first and last 13 sts.</span>
With WS of Leg facing, slip first 13 sts to dpn,
place center 28 sts on holder for Instep, join red
and work in St st across remaining 13 sts. Slip
sts on dpn to right-hand end of needle and
work across sts from dpn (what was the first st
on dpn is now the thirteenth st on the righthand
needle)—26 sts on the right-hand needle.
Working on heel sts only; work even until Heel
flap measures 2 <span class="ch_7">½</span>" from beginning, ending after
a RS row.</p>
<p class="title3" id="i2377g4">Turn Heel</p>
<p class="pgph2 wsp" id="i2377g5">Row 1: P15, p2tog, p1, turn.
Row 2: Slip 1, k5, k2tog, k1, turn.
Row 3: Slip 1, purl to 1 st before gap, p2tog (the sts on either side of gap), p1, turn.
Row 4: Slip 1, knit to 1 st before gap, skp (the sts on either side of gap), k1, turn.
Repeat Rows 3 and 4 until all sts have been worked—16 sts remain.
Work 1 WS row even.</p>
</div>
<div id="u1c0c">
<p class="title3" id="i2377h">Heel Gusset</p>
<p class="pgph2" id="i2377i">Change to dpns; join dark green. Continuing in
St st, work across first 8 sts of heel, transfer these
sts onto spare needle; with Needle 1, work across
next 8 sts, on same needle, pick up and knit
10 sts along left side of heel Flap—18 sts on
Needle 1; transfer 28 sts from st holder to Needles
2 and 3—14 sts each needle; with Needle 4,
pick up and knit 10 sts along right side of heel
Flap, work across 8 sts from spare needle—18 sts
on Needle 4; pm for beginning of rnd.</p>
<p class="pgph2" id="i2377j"><i>Note: From this point on, follow the color 
sequence on Foot Chart. In order to minimize the disruption of the color patterns at the beginning and end of each round, work a Jogless Color Change (see Special Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 175)</span>.</i></p>
<p class="pgph2 wsp" id="i2377k">Knit 1 rnd.
Shape Gusset: Beginning with Rnd 3 of Foot
Chart, work decreases as follows:
Rnd 1: On Needle 1, work across to last 3 sts,
k2tog, k1; work across Needles 2 and 3; on
Needle 4, k1, ssk, work to end.
Rnd 2: Continuing to follow Chart for color
sequence, work one rnd even.
Repeat Rnds 1 and 2 until there are 13 sts each
on Needles 1 and 4 and 14 sts each on Needles
2 and 3—54 sts total.</p>
<p class="title3" id="i2377p">Foot</p>
<p class="pgph2" id="i2377q">Work even until Row 33 of Foot Chart is complete.</p>
<p class="title3" id="i2377r">Shape Toe</p>
<p class="pgph2" id="i2377s">Rnd 1 — Decrease Rnd:  *  On Needle 1, work to last 3 sts, k2tog, k1; on Needle 2, k1, ssk, work across; repeat from  *  on Needles 3 and 4.</p>
<p class="pgph2" id="i2377t">Rnd 2: Continuing to follow Foot Chart for color 
sequence, work one rnd even.</p>
<p class="pgph2" id="i2377u">Repeat Rnds 1 and 2 until 18 sts remain after 
a Decrease rnd.</p>
<p class="pgph2" id="i2377v">With Needle 4, work sts on Needle 1; slip sts on Needle 2 onto Needle 3; graft sts together using Kitchener st (see Special Techniques, page 176).</p>
<p class="title3" id="i2377w">Finishing</p>
<p class="pgph2" id="i2377x">Weave in all loose ends. With color of choice, sew open seam closed. Using red, chartreuse, and cream, crochet or braid a loop and attach at back of Leg for hanging the Stocking. Lightly steam-block the completed Stocking.</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="image"><img alt="image" class="full" src="images/0046.jpg"/></p>
<p class="image"><img alt="image" class="full" src="images/0047.jpg"/></p>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div id="u1eda">
<a id="AranTreeSkirt"></a><p class="name red" id="i2442a">Teva Durham</p>
<p class="h1 lime" id="i2442a2"> Aran Tree Skirt and Stocking</p>
</div>
<div id="u1ef1">
<p class="intro" id="i1576a">What better way is there for a knitter to honor her solstice evergreen than to dress it up in a thick, cabled skirt and hang a matching stocking nearby? A bulky Icelandic wool yarn is doubled to give these pieces an ultra-handcrafted look and to speed up the knitting.</p>
</div>
<div id="u1ebf">
<p class="title2" id="i2052a">Finished Measurements</p>
<p class="pgph1" id="i2052b"><span class="ch_8">Tree Skirt</span>: 55" diameter</p>
<p class="pgph1" id="i2052c"><span class="ch_8">Stocking</span>: 10" wide opening; 22" long</p>
<p class="title2" id="i2052d">Yarn</p>
<p class="pgph1" id="i2052e">Reynolds Lopi (100% Icelandic wool; 
117 yards / 3.5 ounces): #718 off-white (MC) 
and #367 mauve-gray (CC) (use double 
strand throughout)</p>
<p class="pgph1" id="i2052f"><span class="ch_8">Tree skirt</span>: 17 balls MC; 4 balls CC</p>
<p class="pgph1" id="i2052g"><span class="ch_8">Stocking</span>: 3 balls MC; 1 ball CC</p>
<p class="title2" id="i2052h">Needles</p>
<p class="pgph1" id="i2052i">One 24" circular (circ) needle size US 13 (9 mm)</p>
<p class="pgph1" id="i2052j">One 16" circular needle size US 13 (9 mm)</p>
<p class="pgph1" id="i2052k">Change needle size if necessary to 
obtain correct gauge.</p>
<p class="title2" id="i2052l">Notions</p>
<p class="pgph1" id="i2052m">Two cable needles (cn), yarn needle, stitch marker, crochet hook size K/10.5 (6.5 mm)</p>
<p class="title2" id="i2052n">Gauge</p>
<p class="pgph1" id="i2052o">10 sts and 16 rows = 4" (10 cm) in Stockinette stitch (St st) using 2 strands held together</p>
</div>
<div id="u1ed6">
<p class="title2 red" id="i2052p">Notes</p>
<p class="pgph1" id="i2052q">•Tree Skirt is worked from radius of circle
around, using Short Rows to shape wedge-
like segments. On each Row 1 of Chart
(after the first repeat), knit the wraps
together with the wrapped sts (see Special
Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 179</span>) for all knit sts; the
wraps on the purl sts may be worked with
the wrapped sts if desired, but it is not
necessary as these wraps blend in.</p>
<p class="pgph1" id="i2052r">•	The Tree Skirt Cable Chart shows one 
segment; the Cable repeats are designed 
to meet between segments.</p>
<p class="pgph1" id="i2052s">•	The contrast color (CC) trim is worked 
at the same time, using Intarsia method (See Special Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 177</span>). 
It is then extended along CO and BO edges to form a tie to go around the tree trunk.</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="image"><img alt="image" class="full" src="images/0049.jpg"/></p>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div id="u1fa2">
<p class="title" id="i2052t">Tree Skirt

</p>
<p class="pgph2" id="i2052u"><span class="ch_8">Segment 1: </span>Using 24" circ needle and 2 strands MC held together, CO 67 sts; using 
2 strands CC held together, CO 6 sts—73 sts total. (RS) Working back and forth, begin Cable Tree Skirt Chart Row 1, working Sts 1 – 6 
with CC and Sts  7 –73 with MC, twisting yarn between colors to prevent holes.</p>
<p class="pgph2" id="i2052v">Short Row Shaping: On Row 5 of Chart, 
begin Short Rows as follows:</p>
<p class="pgph2" id="i2052w">(RS) Work across to last st shown on Chart, wrap next stitch, turn (wrp-t).</p>
<p class="pgph2" id="i2052x">(WS) Leaving 3 sts unworked at end of row, work to end as indicated on Chart.</p>
<p class="pgph2" id="i2052y">Continue in this manner through Row 50 
of Chart.</p>
<p class="pgph2" id="i2052z"><span class="ch_8">Segment 2: </span>Working wraps together with wrapped sts as explained in Notes at left, begin Row 1 of Chart across all sts. Work Rows 1 – 50 as for Segment 1.</p>
<p class="pgph2" id="i2052aa"><span class="ch_8">Segments </span><span class="ch_8">3</span><span class="ch_8">–10: </span> Work as for Segment 2.</p>
<p class="pgph2" id="i2052ab"><span class="ch_8">Segment 11 </span> (final segment): Work as for Segment 2 through Row 40, to make the angle of the edge less severe (to better match the CO row); BO while working Row 1 of Chart, working wraps together with wrapped sts.</p>
<p class="title3" id="i2053a">Finishing</p>
<p class="pgph2" id="i2053b"><span class="ch_10">Ties:</span> Using 2 strands CC, pick up and knit 6 sts at end or beginning of CC Cable. Continue in 
pattern as established until tie is length of open edge, plus 16" more, ending with 4 rows 
in St st. BO all sts. Sew cable trim along open edge, leaving remaining 16" free. Repeat on other side. Weave in ends. Block lightly.</p>
</div>
<p class="image"><img alt="image" class="full" src="images/0050.jpg"/></p>
<div id="u1fb2">
<p class="title" id="i2053c">Stocking

</p>
<p class="pgph2" id="i2053d">Using 16" circ needle and 2 strands MC held
together, CO 48 sts. Join for working in the rnd,
being careful not to twist sts; place marker (pm)
for beginning of rnd.<br/>
Work Rnds 1–39 of Leg Chart, shaping
as indicated on Chart—36 sts remain.<br/>
Rnd 40: Divide for heel—work around to 5 sts
before the end of the rnd; slip last 20 sts
worked onto st holder for Instep (see Chart).
heel will be worked on remaining 16 sts.</p>
<p class="title3" id="i2053g">Heel</p>
<p class="pgph2" id="i2053h">Row 1 (RS):  *  P2tog, [k1, p1] twice, k1; repeat from  *  once, p2tog, tur n — 13 sts remain.</p>
<p class="pgph2" id="i2053i">Row 2: Work in k1, p1 rib—knit the knits and purl the purls as they face you.</p>
<p class="pgph2" id="i2053j">Repeat Row 2 until Heel measures  2 <span class="ch_7">½</span>" from Dividing Row, ending with a WS row.</p>
<p class="title3" id="i2053k">Turn Heel</p>
<p class="pgph2" id="i2053l">(RS) Change to St st, work 8 sts, wrp-t; work 3 sts, wrp-t;  *  work to one st past previous wrap, wrp-t; repeat from  *  until 3 wraps have been worked at each side, working to end of WS row.</p>
</div>
<div id="u1fa6">
<p class="title3" id="i2053m">Instep and Foot</p>
<p class="pgph2" id="i2053n">(RS) K13 heel sts; pick up and knit 6 sts along
side edge of heel Flap; work across 20 sts of Instep, following Rnd 41 of Chart; pick up and
knit 6 sts along opposite side edge of heel Flap—45 sts.<br/>
Continue even as established, working St st
on Sole, Cables and Rev St st on Instep,
with 2 St sts on either side of Instep, until foot
measures 7" from heel or desired length,
and AT THE SAME TIME,</p>
<p class="pgph2" id="i2053p"><span class="ch_8">Shape Foot:</span> Decrease Rnd—Knit to 1 st
before Instep sts; k2tog, work 20 Instep sts,
ssk; knit to end of rnd. Repeat Decrease Rnd
every other rnd 6 times total—30 sts remain
(13 for the Sole, 17 for the Instep, after working
all decreases from Chart).</p>
<p class="title3" id="i2053p2">Toe</p>
<p class="pgph2" id="i2053q2">K11, k2tog, pm, ssk, p13, k2tog, pm, ssk—28 sts remain.
Decrease Rnd: * Work to 2 sts before marker, k2tog, ssk; repeat from * around. Repeat
Decrease Rnd until 12 sts remain. Graft Toe sts together with Kitchener st (see Special Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 178</span>).</p>
<p class="title3" id="i2053s2">Finishing</p>
<p class="pgph2" id="i2053t2"><span class="ch_8">Cable Trim:</span> Using 2 strands CC, CO 6 sts.
Work Cable as for Sts 1– 6 of Leg Chart until
piece measures same as CO edge, ending
with 4 rows St st. BO all sts.<br/>
Beginning at center back of Stocking, sew
Cable trim around edge, then graft CO and BO
sts together. Weave in ends. Block lightly.<br/>
Using crochet hook and single strand CC, work
a 5" crochet chain (see Special Techniques,
<span href="#/Techniques" title="Link target outside of Sample">page 176</span>); fasten off, leaving a tail for attaching
chain to Stocking. Sew chain to inside center
back of trim.</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="image"><img alt="image" class="full" src="images/0052.jpg"/></p>
<p class="image"><img alt="image" class="full" src="images/0053.jpg"/></p>
<div id="u21a5">
<a id="mrsClaus"></a>
<p class="name brown" id="i557a">Cindy Taylor</p>
<p class="h1 red" id="i557a2">Mrs. Claus Doll</p>
</div>
<div id="u218e">
<p class="intro" id="i1924a">Santa may get all the credit on Christmas Eve, but we know that behind nearly every great man, there's a woman who's really running the show. It's time to give Mrs. Claus her due. After generations of keeping the elves in line, collating the Christmas lists, and baking enough cookies to feed an army, she's honored here by Cindy Taylor in a very soft, very cuddly doll, complete with knitted-on high-heeled boots and a choice of petticoats and dresses. Mrs. Claus does not scrimp 
on style.</p>
</div>
<div id="u218a">
<p class="title2" id="i207a">Finished Measurement</p>
<p class="pgph1" id="i207b">Approximately 16" tall</p>
<p class="title2" id="i207c">Yarn</p>
<p class="pgph1" id="i207d">Mission Falls 1824 Wool (100% wool; 85 yards / 50 grams): 1 skein each in black (A), pink (D), blue (E), wine (F), olive (G), and gold (H)</p>
<p class="pgph1" id="i207e">Mission Falls 1824 Cotton (100% cotton; 
84 yards / 50 grams): 1 skein each in ivory (B) 
and flax (C)</p>
<p class="pgph1" id="i207f"><span class="ch_8">Shown in the following colors:</span>Boots (A); Body (B); hair (C); solid dress (D); 
2-color dress (G and H); petticoat (D, E, and F)</p>
<p class="title2" id="i207g">Needles</p>
<p class="pgph1" id="i207h">One set of five double-pointed needles (dpn) 
size US 7 (4.5 mm)</p>
<p class="pgph1" id="i207i">Change needle size if necessary to obtain 
correct gauge.</p>
<p class="title2" id="i207j">Notions</p>
<p class="pgph1" id="i207k">Yarn needle, polyester fiberfill, crochet hook size H/8 (5 mm), 2 buttons for dress, scrap 
embroidery floss for eyes and mouth, embroidery needle,  1<span class="ch_6"> ¼</span>" plastic ring for hair bun</p>
<p class="title2" id="i207l">Gauge</p>
<p class="pgph1" id="i207m">16 sts and 24 rows = 4" (10 cm) in Stockinette stitch (St st)</p>
</div>
<div id="u21bc">
<p class="title" id="i207n">Doll</p>
<p class="title3" id="i207o">Boots  <span class="pgph2 black">(make 2)</span></p>
<p class="pgph2" id="i207p"><span class="ch_8">Boot Heel</span></p>
<p class="pgph2 wsp" id="i207q">Using 2 dpn and A, CO 9 sts; begin St st. (WS)
Beginning with a purl row, work even for 5 rows; cut yarn, leaving heel sts on needle.
<span class="ch_8">First Side:</span> Continuing with A, on same needle,
CO 6 sts. Working on these 6 sts only:
Row 1 (WS): Purl across to last st, M1, p1.
Row 2: K1, M1, knit to end.
Repeat Rows 1 and 2 once—10 sts.
(WS) Purl side sts, then heel sts—19 sts total.
<span class="ch_8">Second Side:</span> Using A, on a separate needle,
CO 6 sts. Working on these 6 sts only:
Row 1 (WS): P1, M1, purl to end.
Row 2: Knit across to last st, M1, k1.
Repeat Rows 1 and 2 once—10 sts.
(WS) Continuing with first needle, purl sts of second side—29 sts.
<span class="ch_8">Toe Shaping:</span> Continue on all Boot sts;
Row 1 (RS): K1, ssk, knit across to last 3 sts, k2tog, k1.
Row 2: P1, p2tog, purl across to last 3 sts, p2tog-tbl, p1.
Repeat Rows 1 and 2 once—21 sts.
<span class="ch_8">Boot Top</span>
Divide sts evenly onto 3 needles. Join for working in the rnd; place marker (pm) for beginning of rnd. Work 3 rnds even; break off A.</p>
<p class="title3" id="i208c">Legs <span class="pgph2 black">(make 2)</span></p>
<p class="pgph2" id="i208c2">Join B; continuing in St st, work even for 6". 
BO all sts; sew boot and leg seams; stuff.</p>
<p class="title3" id="i208d">Body</p>
<p class="pgph2" id="i208e">Using B, CO 36 sts; divide evenly onto 3 
needles. Join for working in the rnd, being careful not to twist sts; pm for beginning of rnd. Begin St st. Work even until piece measures 
 4  <span class="ch_7">½</span> " from beginning.</p>
<p class="pgph2" id="i209a"><span class="ch_10">Shape Shoulders:</span> Divide sts evenly onto 2 
needles; turn Body WS out. Using 3-Needle 
BO method (see Special Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 179</span>), 
BO 5 sts at beginning of next 2 rows—16 sts remain for neck; 8 on each needle. <br/>
Turn Body RS out.</p>
<p class="title3" id="i209b">Neck</p>
<p class="pgph2" id="i209c">Dividing sts evenly onto 3 needles on first rnd, knit even for 4 rnds.</p>
</div>
<div id="u2173">
<p class="title3" id="i209d">Head</p>
<p class="pgph2" id="i209e">Divide sts evenly onto 2 needles.
<br/>Rnd 1: On first needle— * K1, M1, knit across to last st, M1, k1; repeat from * on second needle—20 sts.
<br/>Rnd 2: Knit.
<br/>Rnds 3–8: Repeat Rnds 1 and 2—32 sts.
<br/>Rnds 9–12: Knit.
<br/>Rnd 13: On first needle— * K1, ssk, knit to last 3 sts on needle, k2tog, k1; repeat from * on second needle—28 sts remain.
<br/>Rnd 14: Knit.
<br/>Rnds 15–20: Repeat Rnds 13 and 14—16 sts remain.
<br/>Graft remaining sts together using Kitchener st (see Special Techniques, page 176); stuff piece loosely.</p>
<p class="title3" id="i209n">Arms <span class="pgph2 black">(make 2)</span></p>
<p class="pgph2" id="i209o">Using B, CO 18 sts; divide sts evenly onto 3
needles. Join for working in the rnd, being careful
not to twist sts; pm for beginning of rnd.<br/>
Begin St st.<br/>
Work even until piece measures 4" from
the beginning.<br/>
<span class="ch_10">Shape Hands:</span> Divide sts evenly onto 2 needles.
<br/>Rnd 1: On first needle— * K1, M1, knit across
to last st, M1, k1; repeat from * on second
needle—22 sts.
<br/>Rnd 2: Knit.
<br/>Repeat Rnds 1 and 2 once—26 sts.
<br/>Knit even for 4 rnds.</p>
<p class="title3" id="i209v">Hand</p>
<p class="pgph2" id="i209w">Rnd 1: On first needle— * K1, ssk, knit across
to last 3 sts, k2tog, k1; repeat from * on second
needle—22 sts remain.<br/>
Rnd 2: Knit.<br/>
Repeat Rnds 1 and 2 three times—10 sts
remain. Graft sts together using Kitchener st
and stuff piece loosely.</p>
<p class="title3" id="i209z">Thumb</p>
<p class="pgph2" id="i210a">Using B, pick up and knit 6 sts at upper base of
hand; divide evenly onto 3 needles. Join for
working in the rnd; pm for beginning of rnd.<br/>
Begin St st. Work even for 3 rnds.<br/>
Next Rnd: * [K2tog] 3 times *; stuff Thumb.<br/>
Knit 1 rnd even.<br/>
Next Rnd: K1, k2tog.<br/>
Break off yarn. Thread through remaining sts; pull to close; fasten off.</p>
<p class="title3" id="i210f">Finishing</p>
<p class="pgph2" id="i210g">Sew arms and legs to body, being careful to 
position hands and feet correctly.</p>
<p class="pgph2">To make hair, using C, beginning and ending at
end of head increases (see photo), stitch
through and wrap outer perimeter of head.</p>
<p class="pgph2">
To make bun, using plastic ring, crochet hook,
and C, work single crochet (sc) around the ring.
Using yarn needle threaded with C, wrap ring
with yarn, going in and out of center, until bun is
approximately 2" in diameter; attach Bun by
sewing to back of hairline.</p>
<p class="pgph2">
Using embroidery floss, sew eyes and mouth 
as desired (see photo).</p>
</div>
<p class="image"><img alt="image" class="full" src="images/0055.jpg"/></p>
<div id="u2286">
<p class="title" id="i210l">
Clothes</p>
<p class="title3" id="i210m">Petticoat</p>
<p class="pgph2" id="i210n">Using color of choice, CO 48 sts; divide evenly
over 3 needles. Join for working in the rnd;
pm for beginning of rnd. Begin St st.<br/>
Work even until piece measures 4" from the beginning, ending 2 sts before marker on last row.
<br/><span class="ch_10">Shape Armholes:</span> Divide sts evenly onto 2
needles. * BO off 4 sts (1 st remaining on needle);
using same needle, k19—20 sts on needle.
<br/>Repeat from * with second needle; turn.
<br/><span class="ch_10">Front and Back Yoke</span> (both alike)
<br/>Working on second needle sts only,
<br/>Row 1 (WS): * Purl.
<br/>Row 2: K1, ssk, knit to last 3 sts, k2tog, k1.
<br/>Repeat Rows 1 and 2 twice—14 sts remain.
<br/>Rows 7–9: Work even in St st.
<br/>BO all sts, leaving a 10" tail.
<br/>Repeat from * for sts on first needle.
<br/><span class="ch_10">Join Shoulders:</span> Using crochet hook and
10" tail, chain (ch) 10; join with slip st to
opposite side.</p>
</div>
<div id="u228a">
<p class="title3 black" id="i211a">Crochet Trim</p>
<p class="pgph2" id="i211b"><span class="ch_10">Armhole:</span> *  Join color of choice at center of 
the underarm (4 BO sts). Sc in next 2 sts, half 
double crochet (hdc) up side of armhole to shoulder chain; sc in each chain; hdc down side of armhole, sc in last 2 sts. Fasten off. Repeat from  *  for remaining armhole.</p>
<p class="pgph2" id="i211c"><span class="ch_10">Edging:</span>  Join color of choice in “side seam” st on lower edge.</p>
<p class="pgph2" id="i211d">Rnd 1: Ch 1, sc in each st around; join with slip st to first st — 48 sc.</p>
<p class="pgph2" id="i211e">Rnd 2: Ch 3 (counts as double crochet [dc]), dc each sc around; join with slip st to top of ch-3.</p>
<p class="pgph2" id="i211f">Rnd 3: [Ch 3, slip st in next st] around; join with slip st to ch-3.</p>
<p class="title3" id="i211g">Dress</p>
<p class="pgph2" id="i211h"><span class="ch_8">Skirt</span> 
Using color of choice, (D [solid]; G [2-color] as
shown), CO 90 sts; divide evenly onto 3 nee-
dles. Join for working in the rnd, being careful
not to twist sts; pm for beginning of rnd.<br/>
Begin St st.<br/>
Work even for 4 rnds.</p>
<p class="pgph2" id="i211j"><span class="ch_10">Eyelet Rnd:</span> *  Yo, k2tog; rep from  *  around.</p>
<p class="pgph2" id="i211k">Knit 1 rnd even.</p>
<p class="pgph2" id="i211l">Begin Cat’s Paw Chart, Rnd 1. Working 9-st 
repeat 10 times around, work Rows 1 – 6 once.</p>
<p class="pgph2" id="i211m">Repeat Eyelet Rnd once.</p>
<p class="pgph2" id="i211n">Work even for 4 rnds.</p>
<p class="pgph2" id="i211o"><span class="ch_8">Bodice</span>
<br/>Using color of choice, (D [solid]; H [2-color] 
as shown), continue as follows:</p>
<p class="pgph2" id="i211p">Rnd 1:  *  K1, k2tog; repeat from  *  around— 60 sts remain, and AT THE SAME TIME,</p>
<p class="pgph2" id="i211q"><span class="ch_8">Solid version:</span>  Work Rnd 1, then work 2 
rnds even.</p>
<p class="pgph2" id="i211r"><span class="ch_8">2-color version:</span> Work Rnd 1 in color pattern
as follows: K3 G, k1 h, * k5 G, k1 h; repeat
from * to last 2 sts, end k2 G, then work Rnds 2
and 3.
<br/>Rnd 2: K2 G, * k3 h, k3 G; repeat from * to last
4 sts, k3 h, k1 h.
<br/>Rnd 3: K1 G * k5 h, k1 G; repeat from * to last
5 sts, k5 h.</p>
<p class="pgph2" id="i211u"><span class="ch_10">Front Opening:</span>  (Both versions) Continuing in Bodice color as established,</p>
<p class="pgph2" id="i211v">Row 1 (RS): BO 2 sts, knit to end of row, turn.</p>
<p class="pgph2" id="i211w">Row 2: Purl.</p>
<p class="pgph2" id="i211x">Row 3: Knit.</p>
<p class="pgph2" id="i211y2">Repeat Rows 2 and 3 until piece measures 4 <span class="ch_7">½</span>"</p>
<p class="pgph2" id="i211y"> from CO edge, ending with a WS row.</p>
</div>
<p class="image"><img alt="image" class="full" src="images/catspaw_chart.jpg"/></p>
<div id="u227d">
<p class="pgph2" id="i212a"><span class="ch_10">Shape Armholes:</span> K11 for right Front; BO 2 sts for armhole; k24 for Back; BO 2 sts for armhole (1 st remaining on needle); k10 for left Front.</p>
<p class="pgph2" id="i212b"><span class="ch_8">Left Front</span></p>
<p class="pgph2" id="i212c">Working on left Front sts only:</p>
<p class="pgph2" id="i212d">Row 1 and all WS rows: Slip 1, purl to end.</p>
<p class="pgph2" id="i212e">Row 2: K1, ssk, k8—10 sts remain.</p>
<p class="pgph2" id="i212f">Row 4: K1, ssk, k7—9 sts remain.</p>
<p class="pgph2" id="i212g">Row 6: Knit across to last 3 sts, k2tog, k1.</p>
<p class="pgph2" id="i212h">Row 7: Repeat Row 1.</p>
<p class="pgph2" id="i212i">Repeat Rows 6 and 7 twice—6 sts remain. Place remaining sts on holder.</p>
<p class="pgph2" id="i212j"><span class="ch_8">Back </span></p>
<p class="pgph2" id="i213a">Working on Back sts only:</p>
<p class="pgph2" id="i213b">Row 1 (WS): Join yarn and purl to end.</p>
<p class="pgph2" id="i213c">Row 2: K1, ssk, knit across to last 3 sts, k2tog, k1—22 sts remain.</p>
<p class="pgph2" id="i213d">Row 3: Purl.</p>
<p class="pgph2" id="i213e">Rows 4 and 5: Repeat Rows 2 and 3—20 sts remain after Row 4.</p>
<p class="pgph2" id="i213f">Row 6: Knit.</p>
<p class="pgph2" id="i213g">Row 7: Purl.</p>
<p class="pgph2" id="i213h">Repeat Rows 6 and 7 twice. Place remaining sts on holder.</p>
<p class="pgph2" id="i213i"><span class="ch_8">Right Front</span></p>
<p class="pgph2" id="i213j">Working on right Front sts only:</p>
<p class="pgph2" id="i213k">Row 1 (WS): Join yarn and purl to end.</p>
<p class="pgph2" id="i213l">Row 2: Slip 1, knit across to last 3 sts, k2tog, k1—10 sts remain.</p>
<p class="pgph2" id="i213m">Row 3: Purl.</p>
<p class="pgph2" id="i213n">Rows 4 and 5: Repeat Rows 2 and 3—9 sts—remain after Row 4.</p>
<p class="pgph2" id="i213o">Row 6: Slip 1, ssk, knit to end—8 sts remain.</p>
<p class="pgph2" id="i213p">Row 7: Purl.</p>
<p class="pgph2" id="i213q">Repeat Rows 6 and 7 twice—6 sts remain.</p>
<p class="pgph2" id="i213r">Turn dress WS out; work 3-needle BO over 6 sts at each shoulder, keeping center 8 sts of</p>
</div>
<div id="u2282">
<p class="pgph2" id="i213r2">Back on holder for neck. Turn dress RS out.</p>
<p class="pgph2" id="i213s"><span class="ch_8">Sleeves</span></p>
<p class="pgph2" id="i213t">Using Bodice color, with RS facing, beginning 
at center of underarm, pick up and knit 25 sts evenly around armhole; divide evenly onto 3 needles. Join for working in the rnd; pm for 
beginning of rnd.</p>
<p class="pgph2" id="i213u"><span class="ch_8">Puff Sleeves</span> (solid color version)</p>
<p class="pgph2" id="i213v">Rnd 1: K9, k1-f/b of next 7 sts, k9—32 sts.</p>
<p class="pgph2" id="i213w">Rnds 2 – 4: Knit.</p>
<p class="pgph2" id="i213x">Rnd 5:  *  K1, ssk, knit to last 3 sts, k2tog, k1—30 sts remain.</p>
<p class="pgph2" id="i213y">Rnds  6–9: Knit.</p>
<p class="pgph2" id="i213z">Rnds 10–14: Repeat Rnds  5– 9—28 sts remain after Rnd 10.</p>
<p class="pgph2" id="i213aa">Rnds 15 and 16: Repeat Rnds 5 and 6—26 sts remain after Rnd 15.</p>
<p class="pgph2" id="i213ab">Bind off remaining sts.</p>
<p class="pgph2" id="i213ac"><span class="ch_8">Straight Sleeves</span> (2-color version)</p>
<p class="pgph2" id="i213ad">Work even for 16 rnds; BO remaining sts.</p>
<p class="title3" id="i213ae">Finishing</p>
<p class="pgph2" id="i214a"><span class="ch_10">Crochet Trim for Sleeves</span></p>
<p class="pgph2" id="i214b">Rnd 1: Using Sleeve color, or color of choice and crochet hook, sc evenly around lower edge of Sleeve; join with Slip st to first st.</p>
<p class="pgph2" id="i214c">Rnd 2:  *  Ch 3, Slip st in next st; rep from  *  around; join with Slip st to first st. Fasten off.</p>
<p class="pgph2" id="i214d"><span class="ch_10">Crochet Trim for Skirt</span></p>
<p class="pgph2" id="i214e">Rnd 1: Using Skirt color or color of choice and crochet hook, sc evenly around lower edge of skirt; join with Slip st to first st—90 sc.</p>
<p class="pgph2" id="i214f">Rnd 2:  *  Work Shell st [sc, hdc, dc, hdc, sc in same st] in next sc, ch 1, skip 2 sc; repeat from  *  around. Fasten off.</p>
<p class="pgph2" id="i214g"><span class="ch_10">Crochet Trim for Neck Opening</span></p>
<p class="pgph2" id="i214h">Row 1: With RS facing, beginning at center of  Right Front neck opening, sc evenly around neck, ending at center  l eft Front, including sts on holder for Back neck. Fasten off.</p>
<p class="pgph2" id="i214i"><span class="ch_10">Buttonholes:</span> To make 2 buttonholes, with RS facing, beginning at center of  Right Front neck as Row 1, slip st in first sc, ch 3, skip 2 sc, slip st in next 6 sc, ch 3, skip 2 sc, slip st in next sc. Fasten off.</p>
<p class="pgph2" id="i214j">When weaving in ends, tack lower edge of  Right Front band over  Left Front band. Sew buttons opposite chain loop buttonholes.</p>
</div>
<p class="image"><img alt="image" class="full" src="images/0057.jpg"/></p>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div id="u2409">
<a id="tableRunner"></a><p class="name pink" id="i453a">VÉronik Avery</p>
<p class="h1 dPurple" id="i453a2"> Winter Solstice Table Runners</p>
</div>
<div id="u2420">
<p class="intro" id="i264a">These sophisticated table runners are a beautiful way to ”dress“ the holiday table. The center gray one is embellished with sashiko—a Japanese form of running-stitch embroidery—in a harmonious design that can be interpreted in many ways. <span class="wsn1">The intersecting circles can recall the arc of the sun’s path, the cycles of the seasons, the encircling embrace of a loved one, or even poinsettias. The lace pattern of the two taupe side table runners resemble candle flames; the optional embroidered stars are inspired by huck embroidery.</span></p>
</div>
<div id="u2401">
<p class="title2" id="i2086a">Finished Measurements</p>
<p class="pgph1" id="i2086b"><span class="ch_8">Sashiko version:</span> 
Approximately 16" wide by 55" long</p>
<p class="pgph1" id="i2086c"><span class="ch_8">Openwork version:</span> 
Approximately 16" wide by 48" long</p>
<p class="title2" id="i2086d">Yarns</p>
<p class="pgph1" id="i2086e">Louet Gems Merino Opal (100% merino wool; 112 yards / 50 grams):</p>
<p class="pgph1 wsp" id="i2086f"><span class="ch_8">Sashiko version:</span> 
7 skeins #43 taupe (MC)</p>
<p class="pgph1 wsp" id="i2086g"><span class="ch_8">Openwork version:</span> 
6 skeins #36 linen grey (MC) </p>
<p class="pgph1"><span class="ch_8">Both versions:</span>
<br/>Louet Euroflax Sportweight (100% linen;
135 yards / 50 grams): 2 skeins #18.36
natural (CC)</p>
</div>
<p><img alt="no background image." class="full" src="images/0058_chart1.jpg"/></p>
<div id="u23ea">
<p class="title2" id="i2086h">Needles</p>
<p class="pgph1" id="i2086i">One 40" circular (circ) needle size US 3 
(3.25 mm)</p>
<p class="pgph1" id="i2086j">One 40" circular needle size US 2 
(2.75 mm)</p>
<p class="pgph1" id="i2086k">Change needle size if necessary to 
obtain correct gauge.</p>
<p class="title2" id="i2086l">Notions</p>
<p class="pgph1" id="i2086m"><span class="ch_8">Both versions:</span> Yarn needle</p>
<p class="pgph1" id="i2086n"><span class="ch_8">Sashiko version only:</span> Tissue paper, 
10" wide by 54" long, dressmaker’s 
tracing wheel, sharp, large-eyed 
embroidery needle</p>
<p class="title2" id="i2086o">Gauge</p>
<p class="pgph1" id="i2086p">24 sts and 36 rows = 4" (10 cm) in Openwork pattern, using larger needles and MC</p>
<p class="pgph1" id="i2086q">25 sts and 34 rows = 4" (10 cm) in Stockinette stitch (St st), using larger needles and MC</p>
</div>
<div id="u2405">
<p class="title2 red" id="i2086r">Notes</p>
<p class="pgph1" id="i2086s"> •<span class="ch_8"> Openwork Stitch Pattern</span></p>
<p class="pgph1" id="i2086t">(Multiple of 12 sts + 3; 16-row repeat)
<br/>(See Chart)
<br/>Row 1 and all WS rows: Purl.
<br/>Row 2 (RS): K1 (edge st), * k1, yo, k2,
k2tog, k3, ssk, k2, yo; repeat from * across,
end k2 (edge sts).
<br/>Row 4: K1, * k2, yo, k2, k2tog, k1, ssk, k2,
yo, k1; repeat from * across, end k2.
<br/>Row 6: K1, * k3, yo, k1, k2tog, k1, ssk, k1,
yo, k2; repeat from * across, end k2.
<br/>Row 8: Knit.
<br/>Row 10: K1, * k2, ssk, k2, yo, k1, yo, k2,
k2tog, k1; repeat from * across, end k2.
<br/>Row 12: K1, * k1, ssk, k2, yo, k3, yo, k2,
k2tog; repeat from * across, end k2.
<br/>Row 14: K1, * k1, ssk, k1, yo, k5, yo, k1,
k2tog; repeat from * across, end k2.
<br/>Row 16: Knit.
<br/>Repeat Rows 1–16 for Openwork pattern.</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="image"><img alt="image" class="full" src="images/0059.jpg"/></p>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div class="aside" id="u258d" style=" background-color:#d8dfec; padding:2em;">
<p class="name dPurple" id="i1091a"><b>Winter Solstice</b></p>
<p class="intro" id="i1091b">On or around December 22,
in the thick of the holiday season,
comes the winter solstice scientifically speaking, the point at which
the earth is tilted so the northern
hemisphere receives the least amount
of sunlight. While it is the shortest
day of the year in this half of the
world, it is also the day that marks
the return of the sun; every day after
it will be longer. For many people,
from ancient times to the present,
this turning point has been a cause
for celebration as it marks the
subtle exchange of darkness for light,
cold for warmth, despair for joy.
In fact, many of our modern holiday
traditions hark back to this natural
event lighting candles, Christmas
tree lights, and fires; bringing
greenery and special plants into our
homes; and feasting in celebration
of harvest abundance. Even gift-
giving stems from the solstice event,
a show of appreciation for the
company of family and friends
during a dark, cold season a good
thing to remember when battling
the shopping crowds.</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div id="u255f">
<p class="image"><img alt="image" class="full" src="images/0060_a.jpg"/></p>
<p class="photoCap" id="i631a">Sashiko Table Runner</p>
</div>
<div id="u255b">
<p class="title" id="i2087b">Sashiko Table Runner

</p>
<p class="pgph2" id="i2087c">Using Long-Tail CO method (see Special
Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 178</span>) and MC, CO 91 sts;
begin St st. Work even until piece measures
52" from the beginning, ending with a RS row.
BO all sts using Outline Stitch BO (see Special
Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 178</span>).</p>
<p class="title" id="i2087d">
Openwork Table Runner

</p>
<p class="pgph2" id="i2087e">Using Long-Tail CO method (see Special
Techniques, <span href="#/Techniques" title="Link target outside of Sample">page 178</span>) and MC, CO 87 sts;
begin Openwork pattern. Work Rows 1–16
of pattern 23 times—368 rows. BO all sts
using Outline Stitch BO.</p>
<div id="u2576">
<p class="image"><img alt="image" class="full" src="images/0060_b.jpg"/></p>
<p class="photoCap" id="i2628a">Openwork Table Runner</p>
</div>
</div>
<div id="u2557">
<p class="title3" id="i2087f">Finishing  <span class="pgph2 black">(both versions)</span> </p>
<p class="pgph2" id="i2087f2">With yarn threaded on a tapestry needle,
weave in loose ends. Wet-block to finished
measurements.
<br/>Apply edging on both versions; numbers in ( )
apply to Sashiko version.
<br/><span class="ch_8">Edging:</span> With RS facing, using larger needles
and CC, pick up and knit 796 (932) sts as
follows: 84 (88) sts in horizontal loops along
CO row, place marker (pm); 314 (378) sts
along side, pm; 84 (88) sts in horizontal loops
along BO row, pm; 314 (378) sts along
remaining side. Join for working in the rnd;
pm to indicate beginning of rnd. 
<br/><span class="ch_5">Note: Slip markers as you come to them.</span>
<br/>Rnd 1: Purl.
<br/>Rnds 2 and 4: [K1-tbl, knit to one st before
marker, k1-tbl] 4 times.
<br/>Rnd 3: [K1-tbl, yo, * k2tog, yo; repeat from * to
one st before marker, k1-tbl] 4 times.
<br/>Rnd 5: [K1-tbl, yo, * k1, slip 1 wyif; repeat from
* to 2 sts before marker, k1, yo, k1-tbl] 4 times.
<br/>Rnd 6: [K1-tbl, * k1, slip 1 wyif; repeat from * to
2 sts before marker, k1, k1-tbl] 4 times.
<br/>Rnd 7: [K1-tbl, yo, k2, * slip 1 wyif, k1; repeat
from * to 2 sts before marker, k1, yo, k1-tbl]
4 times.
<br/>Rnds 8 and 12: [K1-tbl, k1, * k1, slip 1 wyif;
repeat from * to 3 sts before marker, k2, k1-tbl]
4 times.
<br/>Rnd 9: [K1-tbl, yo, * k1, slip 1 wyif; repeat from
* to 2 sts before marker, k1, yo, k1-tbl] 4 times.
<br/>Rnd 10: [K1-tbl, * k1, slip 1 wyif; repeat from *
to 2 sts before marker, k1, k1-tbl] 4 times.
<br/>Rnd 11: [K1-tbl, yo, k1, * k1, slip 1 wyif; repeat
from * to 3 sts before marker, k2, yo, k1-tbl]
4 times.
<br/>Change to smaller needles; purl even for 2 rnds.
<br/>Next Rnd: * P1, slip 1 wyib; repeat from *
to end.
<br/>BO as follows: Slip 2 sts to right-hand needle
and, with tip of left-hand needle, * pass the
first st over the second stitch, slip 1; repeat
from * across.</p>
</div>
<div id="u2516">
<p class="title2 gold" id="i2087v">Sashiko embroidery</p>
<p class="pgph2" id="i2087w">Draw 3 lines on tissue paper: one in the center, lengthwise, and two on either side spaced 1  <span class="ch_7"> ¾</span>" from center. On first and third lines, make marks 1" from bottom, and 13 more spaced 1  <span class="ch_7"> ¾</span>" apart. On center line, make mark 2 <span class="ch_7"> ¾</span>" from bottom, and 13 more spaced 1  <span class="ch_7"> ¾</span>" apart. Using a compass set at 1  <span class="ch_7"> ¾</span>", draw circles on all marks (top and bottom circles on outside lines will be halved). Trace lines using a dressmaker’s tracing wheel. Baste paper pattern to RS of St st runner and, using CC and a sharp embroidery needle and holes left by wheel as guide, embroider design using a running stitch. Remove paper pattern carefully once embroidery is complete and weave in ends. Steam lightly.</p>
<p class="title2 gold" id="i2087x">Openwork embroidery</p>
<p class="pgph2" id="i2087y">Thread a 12" length of CC yarn onto yarn needle. Beginning with a corner lozenge (see Embroidery Chart), locate the center st (pink on the Chart). Bring needle up left side of this stitch, leaving a  3– 4" tail on WS; run through third st to the left (gold on the Chart) and through the st directly above the gold stitch (green on the Chart). Run through center st again. Repeat this step on right-hand side. Continue running linen yarn through marked sts (gold on the Chart), always running yarn through center st after each step. Bring yarn back to WS and knot ends together. Repeat on remainder of runner, leaving neighboring lozenges unworked (see photo). Steam lightly.</p>
</div>
<p class="noindent"> </p>
<p class="image"><img alt="image" class="full" src="images/openwork_chart.jpg"/></p>
<p class="image"><img alt="image" class="full" src="images/0062.jpg"/></p>
<div id="u270a">
<p class="photoCap" id="i114a">Cushion styles shown (clockwise from top left): 
1, 2, 3</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<div id="u26d7">
<a id="cushions"></a><p class="intro" id="i1178a">On a cold winter evening, after a holiday feast or 
a snowball fight, nothing feels better than snuggling down before a crackling fire. When you do, you'll want to surround yourself with these thick, tweedy, frost-patterned cushions in rich, regal colors dressed up with details like 
a twisted cord edging or silky tassels.</p>
</div>
<div id="u2706">
<p class="title2" id="i2460a">Finished Measurements</p>
<p class="pgph1" id="i2460b"><span class="ch_8">Cushions 1 and 2:</span> 20" square;</p>
<p class="pgph1" id="i2460c"><span class="ch_8">Cushion 3:</span> 25" square</p>
<p class="title2" id="i2460d">Yarn</p>
<p class="pgph1" id="i2460e">Jo Sharp Classic DK Wool (100% wool; 
107 yards / 50 grams)</p>
<p class="pgph1" id="i2460f">Jo Sharp Silkroad DK Tweed (85% merino wool /
10% silk / 5% cashmere; 147 yards / 50 grams)</p>
<p class="pgph1" id="i2460g">Jo Sharp Silkroad Aran Tweed (85% merino 
wool / 10% silk / 5% cashmere; 93 yards / 
50 grams) </p>
<p class="pgph1"><span class="ch_8">Cushion 1:</span> Classic DK Wool: 7 balls #323 
antique (ivory; A), 4 balls #322 ginger (B)</p>
<p class="pgph1" id="i2460h"><span class="ch_8">Cushion 2:</span> Silkroad DK Tweed: 5 balls #405 
emporio (A), Classic DK Wool Heather: 2 balls #912 brocade (B)</p>
<p class="pgph1" id="i2460i"><span class="ch_8">Cushion 3:</span> Silkroad Aran Tweed: 6 balls #116 
ivy (A), 3 balls #114 vermouth (ivory; B)</p>
<p class="title2" id="i2460j">Needles</p>
<p class="pgph1" id="i2460k"><span class="ch_8">Cushions 1 and 2:</span> One pair straight needles 
size US 6 (4 mm)</p>
<p class="pgph1" id="i2460l"><span class="ch_8">Cushion 3:</span> One pair straight needles size 
US 8 (5 mm)</p>
<p class="title2" id="i2460m">Notions</p>
<p class="pgph1" id="i2460n"><span class="ch_8">Cushions 1 and 2:</span> One 22" square 
cushion insert</p>
<p class="pgph1" id="i2460o"><span class="ch_8">Cushion 2:</span> Four 4" nylon tassels</p>
<p class="pgph1"><span class="ch_8">Cushion 3:</span> One 26" square cushion insert</p>
<p class="title2" id="i2460p">Gauge</p>
<p class="pgph1" id="i2460q"><span class="ch_8">Cushions 1 and 2:</span> 22 sts and 30 rows = 4" 
(10 cm) in stitch pattern from Chart using smaller needles</p>
<p class="pgph1" id="i2460r"><span class="ch_8">Cushion 3: </span> 18 sts and 24 rows = 4" (10 cm) in stitch pattern from Chart using larger needles</p>
</div>
<div id="u26c0">
<p class="title" id="i2460s">Cushion</p>
<p class="title3" id="i2460t">Front</p>
<p class="pgph2 wsp" id="i2460u1">Using A and smaller (smaller, larger) needles,
CO 110 sts; begin St st and Chart.
(RS) Carrying color not in use loosely across
the WS of piece, work Rows 1–150 from Chart
(see <a href="#/Chapter1#cushions">page 64</a>).
BO all sts loosely.</p>
<p class="title3" id="i2460t2">Back</p>
<p class="pgph2" id="i2460u">Using A and smaller (smaller, larger) needles,
CO 110 sts; begin St st. Work even for 150
rows. BO all sts loosely.</p>
<p class="title3" id="i2460y">Finishing</p>
<p class="pgph2" id="i2460z">Block pieces gently on WS using a warm 
iron over a damp cloth. With RS’s together, 
sew three side seams; turn Cushion RS out. </p>
<p class="title2 gold" id="i2460y2">CUSHION 1</p>
<p class="pgph2" id="i2460aa"><span class="ch_8">Twisted Cord:</span> Using B, cut 4 lengths of yarn 
each approximately 13 feet long, and make a Twisted Cord (see Special Techniques, page 178). Stitch twisted cord along seam on three sides of cushion. Stuff with cushion insert; sew remaining side, leaving a small hole in corner. Stitch remainder of cord along final side, tucking end of cord into hole in seam. Stitch to secure.</p>
<p class="title2 gold" id="i2460y3">CUSHION 2</p>
<p class="pgph2" id="i2460ab"><span class="ch_8">Tassels:</span> Thread loop on tassels through work 
at corners; stitch to secure. Stuff with cushion 
insert and sew remaining side.</p>
<p class="title2 gold" id="i2461a">Cushion 3</p>
<p class="pgph2" id="i2461b">Stuff with cushion insert and sew remaining side.</p>
</div>
<div class="pagebreak">
<p class="noindent"> </p>
</div>
<p class="image"><img alt="image" class="full" src="images/0064.jpg"/></p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Chapter1.vue?2f54bd68